import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import PrintIcon from "@mui/icons-material/Print";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { usePermissoesAcesso } from "../Utils/permissoesAcesso";
import { collection, getDocs, limit, orderBy, query, where } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useHistory, useLocation, useParams } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import { db, storage } from "../Config/firebase";
import RelatorioGuiaDosagemPrint from "./RelatorioGuiaDosagemPrint";
import RelatorioInformacoesTracoExperimentalPrint from "./RelatorioInformacoesTracoExperimentalPrint";
import RelatorioDosagemPrint from "./relatoriodosagemPrint";
import { compressImage } from "../../utils/compressImage";
import "./DetalhesDosagem.css";
import { consultaTenantAtual } from "../Utils/tenant";
import { atualizarDocumentoComMesclagem } from "../Utils/concorrenciaFirestore";

const larguraRecorteFotoDosagem = 1200;
const alturaRecorteFotoDosagem = 800;

function carregarImagemRecorteDosagem(url) {
  return new Promise((resolve, reject) => {
    const imagem = new Image();
    imagem.onload = () => resolve(imagem);
    imagem.onerror = () => reject(new Error("Não foi possível carregar a imagem."));
    imagem.src = url;
  });
}

async function gerarFotoDosagemRecortada({
  url,
  zoom,
  deslocamentoX,
  deslocamentoY,
}) {
  const imagem = await carregarImagemRecorteDosagem(url);
  const canvas = document.createElement("canvas");
  canvas.width = larguraRecorteFotoDosagem;
  canvas.height = alturaRecorteFotoDosagem;
  const contexto = canvas.getContext("2d");

  const escalaBase = Math.max(
    larguraRecorteFotoDosagem / imagem.naturalWidth,
    alturaRecorteFotoDosagem / imagem.naturalHeight
  );
  const escala = escalaBase * zoom;
  const larguraImagem = imagem.naturalWidth * escala;
  const alturaImagem = imagem.naturalHeight * escala;
  const limiteX = Math.max(0, (larguraImagem - larguraRecorteFotoDosagem) / 2);
  const limiteY = Math.max(0, (alturaImagem - alturaRecorteFotoDosagem) / 2);
  const x =
    (larguraRecorteFotoDosagem - larguraImagem) / 2 + deslocamentoX * limiteX;
  const y =
    (alturaRecorteFotoDosagem - alturaImagem) / 2 + deslocamentoY * limiteY;

  contexto.drawImage(imagem, x, y, larguraImagem, alturaImagem);

  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", 0.88)
  );
  if (!blob) throw new Error("Não foi possível recortar a imagem.");

  return new File([blob], "dosagem-recortada.jpg", { type: "image/jpeg" });
}

function formatarDataBR(data) {
  if (!data) return "-";
  if (typeof data === "string" && data.includes("-")) {
    const [ano, mes, dia] = data.split("-");
    return `${dia}/${mes}/${ano}`;
  }
  if (data?.seconds) return new Date(data.seconds * 1000).toLocaleDateString("pt-BR");
  if (data?.toDate) return data.toDate().toLocaleDateString("pt-BR");
  return String(data);
}

function formatarCodigoDosagem(valor = "") {
  const codigo = String(valor).trim().toUpperCase().match(/\d+(?:\.[A-Z0-9]{1,5})?/)?.[0] || "";
  return codigo ? `T${codigo}` : "-";
}

function numero(valor) {
  if (valor === "" || valor === null || valor === undefined) return null;
  const n = Number(String(valorRenderizavel(valor)).replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function numeroInicial(valor) {
  if (valor === "" || valor === null || valor === undefined) return null;

  const texto = String(valorRenderizavel(valor))
    .replace(",", ".")
    .trim();

  const match = texto.match(/-?\d+(\.\d+)?/);

  if (!match) return null;

  const n = Number(match[0]);
  return Number.isFinite(n) ? n : null;
}

function numeroBR(valor, casas = 1) {
  const n = typeof valor === "number" ? valor : numero(valor);
  if (n === null) return "";
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

function codigoMaterial(item = {}) {
  return valorRenderizavel(
    item.codigoFornecedor ||
      item.CodigoFornecedor ||
      item.fornecedorCodigo ||
      item.codigoMaterial ||
      item.CodigoMaterial ||
      item.codigoCadastro ||
      item.CodigoCadastro ||
      item.codigo ||
      item.Codigo ||
      item.cod ||
      item.Cod
  );
}

function obterNumeroRevisao(dosagem = {}) {
  return String(
    dosagem.numeroRevisao ??
      String(dosagem.traco || "").match(/REV\.?\s*(\d+)/i)?.[1] ??
      "0"
  ).replace(/\D/g, "") || "0";
}

function normalizarNomeTraco(codigo, numeroRevisao, valor) {
  const codigoFormatado = formatarCodigoDosagem(codigo);
  const texto = String(valor || "").trim().toUpperCase();
  const revisao = String(numeroRevisao || "0").replace(/\D/g, "") || "0";
  const complemento = texto
    .replace(/^T\d+(?:\.[A-Z0-9]{1,5})?\s*[-/]?\s*/i, "")
    .replace(/^REV\.?\s*\d+\s*[-/]?\s*/i, "")
    .replace(/^-+|-+$/g, "")
    .trim();

  return [codigoFormatado === "-" ? "" : codigoFormatado, `REV.${revisao}`, complemento]
    .filter(Boolean)
    .join("-");
}

function valorRenderizavel(valor, camposPreferenciais = []) {
  if (valor === "" || valor === null || valor === undefined) return "";
  if (typeof valor !== "object") return valor;
  if (valor?.seconds) return formatarDataBR(valor);
  if (valor?.toDate) return formatarDataBR(valor);

  const campos = [
    ...camposPreferenciais,
    "densidadeMedia",
    "mediaMassaUnitaria",
    "massaEspecifica",
    "MassaEspecifica",
    "descricao",
    "Descricao",
    "nome",
    "Nome",
    "codigo",
    "valor",
    "Valor",
  ];

  for (const campo of campos) {
    const item = valor[campo];
    if (item !== "" && item !== null && item !== undefined && typeof item !== "object") {
      return item;
    }
  }

  return "";
}

function materialLinha(item) {
  const densidadeGrupo = item.densidade || {};
  const massaUnitariaGrupo = item.massaUnitaria || {};

  return {
    id: item.id,
  material: valorRenderizavel(
  item.descricao ||
    item.Descricao ||
    item.Descrição ||
    item.descricaoMaterial ||
    item.materialDescricao ||
    item.material ||
    item.amostra
),
    procedencia: valorRenderizavel(item.procedencia || item.fornecedor || item.Fornecedor),
    localColeta: valorRenderizavel(item.localColeta),
    tipoAgregado: item.tipoAgregado || "graudo",
    codigo: codigoMaterial(item),
    materialCadastroId:
      item.materialCadastroId || item.fornecedorId || item.cadastroMaterialId || "",
    imagensMaterial: normalizarImagensDoMaterial(item),

    densidade: valorRenderizavel(
      item.densidadeMedia ||
        densidadeGrupo.densidadeMedia ||
        item.densidade ||
        item.massaEspecifica,
      ["densidadeMedia", "DensidadeMedia", "densidadeSss1", "densidadeSss2"]
    ),

    massaUnitaria: valorRenderizavel(
      item.mediaMassaUnitaria ||
        massaUnitariaGrupo.mediaMassaUnitaria ||
        item.massaUnitaria,
      ["mediaMassaUnitaria", "MediaMassaUnitaria", "massaUnitaria1", "massaUnitaria2"]
    ),

    moduloFinura: valorRenderizavel(
      item.moduloFinura ||
        item.moduloDeFinura ||
        item.mf ||
        item.ModuloFinura
    ),

    diametroMaximo: valorRenderizavel(
      item.diametroMaximo ||
        item.diferencaMaxima ||
        item.dmc ||
        item.DMC ||
        item.DiametroMaximo
    ),

    caracterizacaoCompleta: item,
  };
}

function normalizarImagensDoMaterial(material = {}) {
  const fonte =
    material.ImagensMaterial ??
    material.imagensMaterial ??
    material.ImagemMaterial ??
    material.imagemMaterial ??
    material.ImagemUrl ??
    material.imagemUrl ??
    material.Imagem ??
    material.imagem ??
    [];

  let lista;
  if (Array.isArray(fonte)) lista = fonte;
  else if (typeof fonte === "string") lista = [fonte];
  else if (fonte && typeof fonte === "object") {
    const temUrlDireta = fonte.url || fonte.downloadURL || fonte.link || fonte.src || fonte.path || fonte.storagePath;
    lista = temUrlDireta ? [fonte] : Object.values(fonte);
  } else lista = [];

  return lista
    .map((imagem, index) => {
      if (typeof imagem === "string") {
        return { id: `imagem-${index + 1}`, nome: `Imagem ${index + 1}`, url: imagem, path: "" };
      }

      return {
        ...imagem,
        id: imagem?.id || `imagem-${index + 1}`,
        nome: imagem?.nome || imagem?.name || `Imagem ${index + 1}`,
        url: imagem?.url || imagem?.downloadURL || imagem?.link || imagem?.src || "",
        path: imagem?.path || imagem?.storagePath || "",
      };
    })
    .filter((imagem) => Boolean(imagem.url || imagem.path));
}

function codigoOuTraco(item) {
  return valorOuTraco(codigoMaterial(item));
}

function valorOuTraco(valor) {
  const texto = valorRenderizavel(valor);
  return texto === "" || texto === null || texto === undefined ? "-" : texto;
}

function textoAdicaoSuplementarAditivo(dosagem = {}) {
  const aditivos = Array.isArray(dosagem.aditivosResumo)
    ? dosagem.aditivosResumo
    : [];
  const pesoCimento = numero(dosagem.cimentoPeso) || 0;
  const nomeAditivo = (index) => {
    const aditivo = aditivos[index] || {};
    return String(
      aditivo.descricao || aditivo.Descricao || aditivo.material || `Aditivo ${index + 1}`
    ).trim();
  };
  const volumeAditivo = (percentual) => numeroBR(pesoCimento * (percentual / 100), 1) || "0,0";

  if (dosagem.caldaCimento === true) {
    return [
      "Caso a calda apresente fluidez inferior à classe de consistência especificada, admite-se adição suplementar de aditivo para corrigir a fluidez.",
      `A quantidade do aditivo ${nomeAditivo(0)} não pode ser reduzida sem que se observe retração significativa no topo do corpo de prova.`,
      `Caso as verificações de fluidez observadas no cone de Marsh fiquem abaixo do especificado, poderá ser reduzida a quantidade do aditivo ${nomeAditivo(1)}.`,
    ].join("\n");
  }

  return [
    "Caso o concreto apresente abatimento inferior à classe de consistência especificada, admite-se adição suplementar de aditivo para corrigir o abatimento.",
    "O volume máximo permitido de aditivo utilizado na mistura poderá ser de até 1,8% sobre o peso do cimento, conforme especificado pelo fabricante.",
    `1,8% equivale a ${volumeAditivo(1.8)} litros de aditivo ${nomeAditivo(0)} por m³ de concreto.`,
    `Recomenda-se que o volume máximo do aditivo ${nomeAditivo(1)} seja de até 0,5% sobre o peso de cimento, que equivale a ${volumeAditivo(0.5)} litros por metro cúbico de concreto.`,
  ].join("\n");
}

function textoAdicaoSuplementarAgua(dosagem = {}) {
  if (dosagem.caldaCimento === true) {
    return "Não se admite adição suplementar de água. Água suplementar é aquela adicionada acima da quantidade prevista no estudo de dosagem.";
  }

  return [
    "Não se admite adição suplementar de água. Água suplementar é aquela adicionada acima da quantidade prevista no estudo de dosagem.",
    "Para concretagens distantes recomendamos que o concreto saia da usina próximo do limite superior do \"slump\" evitando desta forma a adição de água na obra, mesmo sendo a folga de água do traço, porém caso seja adicionado girar o balão do caminhão betoneira em velocidade máxima o equivalente a 01 minuto por m³ do concreto.",
    "Fazer a coleta do concreto para moldagem de corpos de prova após o início do descarregamento do concreto, entre 15% a 85% do volume de concreto do caminhão.",
    "Concreto dosado para aplicação em até 5h30m.",
    "As proporções entre, cimento, agregado graúdo e agregado miúdo devem ser utilizadas conforme carta traço. A água retida do traço pode ou não ser utilizada durante a concretagem.",
    "Recomenda-se que no momento da aplicação a temperatura do concreto deve ser de no máximo 35 ºC, conforme item 5.8.1 da NBR 7212.",
  ].join("\n");
}

const OBSERVACOES_IMPORTANTES_PADRAO = [
  {
    titulo: "Adição suplementar de aditivo (NBR 7212 item 5.6.3)",
    texto: "Caso o concreto apresente abatimento inferior à classe de consistência especificada, admite-se adição suplementar de aditivo para corrigir o abatimento.",
  },
  {
    titulo: "Adição suplementar de água (NBR 7212 item 5.6.2)",
    texto: "Não se admite adição suplementar de água. Água suplementar é aquela adicionada acima da quantidade prevista no estudo de dosagem.\nAplicar o concreto com temperatura máxima de 35 °C.",
  },
];
const METODOLOGIAS_PADRAO = [
  "NBR 16887/20 - Concreto - Determinação do teor de ar em concreto fresco - Método pressométrico;",
  "NBR 5738/15 - Concreto - Moldagem e Cura de Corpos de Prova Cilíndricos ou Prismáticos de Concreto;",
  "NBR 5739/18 - Concreto - Ensaio de Compressão de Corpos de Prova Cilíndricos;",
  "NBR 12821/09 - Preparação de Concreto em Laboratório - Procedimento.",
];

function metodologiasDosagem(dosagem = {}) {
  const lista = Array.isArray(dosagem.metodologiasRelatorio)
    ? dosagem.metodologiasRelatorio
    : [];

  return lista.length ? lista : METODOLOGIAS_PADRAO;
}

function observacoesImportantesDosagem(dosagem = {}) {
  const lista = Array.isArray(dosagem.observacoesImportantes)
    ? dosagem.observacoesImportantes
    : [];

  const observacoes = lista.length
    ? lista.map((item) => ({
        titulo: String(item?.titulo || ""),
        texto: String(item?.texto || ""),
        textoManual: item?.textoManual === true,
      }))
    : OBSERVACOES_IMPORTANTES_PADRAO.map((item) => ({ ...item }));

  if (dosagem.usarTextosAutomaticosObservacoes !== true) return observacoes;

  return observacoes.map((item, index) => {
    if (index === 0 && !item.textoManual) return { ...item, texto: textoAdicaoSuplementarAditivo(dosagem) };
    if (index === 1 && !item.textoManual) return { ...item, texto: textoAdicaoSuplementarAgua(dosagem) };
    return item;
  });
}

function textoPreenchido(valor) {
  const texto = String(valorRenderizavel(valor)).trim();
  return texto !== "" && texto !== "-";
}

function valorVazio(valor) {
  return valor === "" || valor === null || valor === undefined;
}

function materialVazio(item) {
  if (!item) return true;
  return ![
    item.descricao,
    item.Descricao,
    item.material,
    item.amostra,
    item.fornecedor,
    item.Fornecedor,
    item.massaEspecifica,
    item.MassaEspecifica,
  ].some(textoPreenchido);
}

function numeroBRBetoneira(valor) {
  const n = typeof valor === "number" ? valor : numero(valor);
  if (n === null) return "";
  const casas = Math.abs(n) >= 100 || Number.isInteger(n) ? 0 : 1;
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

function numeroBRComUnidade(valor, unidade) {
  const texto = numeroBRBetoneira(valor);
  return texto ? `${texto} ${unidade}` : "";
}

function quantidadeCpsDosagem(dosagem) {
  const quantidade = numero(dosagem?.cps);
  return quantidade === null ? 0 : Math.max(0, Math.floor(quantidade));
}

function fotosComMinimo(dosagem) {
  const fotos = Array.isArray(dosagem?.relatorioFotografico)
    ? dosagem.relatorioFotografico
    : [];
  const quantidadeMinima = quantidadeCpsDosagem(dosagem);
  const total = Math.max(quantidadeMinima, fotos.length);

  return Array.from({ length: total }, (_, index) => {
    const foto = fotos[index] || {};
    return {
      ...foto,
      id: foto.id || `foto-${index + 1}`,
      legenda: foto.legenda || `FOTO ${String(index + 1).padStart(2, "0")}`,
      url: foto.url || "",
      path: foto.path || "",
      nomeArquivo: foto.nomeArquivo || "",
    };
  });
}

const ORIGEM_FOTO_AGREGADO = "cadastro-material-agregado";

function fotosAutomaticasDosAgregados(materiais = []) {
  const agregadosIncluidos = new Set();
  const materiaisOrdenados = [...materiais].sort((a, b) => {
    const prioridadeA = a?.tipoAgregado === "miudo" ? 0 : 1;
    const prioridadeB = b?.tipoAgregado === "miudo" ? 0 : 1;
    return prioridadeA - prioridadeB;
  });
  let numeroFoto = 0;

  return materiaisOrdenados.flatMap((material, index) => {
    const imagem = normalizarImagensDoMaterial({
      ImagensMaterial: material.imagensMaterial || material.ImagensMaterial,
    })[0];
    if (!imagem) return [];

    const agregadoId = material.materialCadastroId || material.id || `agregado-${index + 1}`;
    if (agregadosIncluidos.has(agregadoId)) return [];
    agregadosIncluidos.add(agregadoId);
    numeroFoto += 1;

    return [{
      id: `foto-agregado-${agregadoId}`,
      legenda: `FOTO ${String(numeroFoto).padStart(2, "0")}`,
      url: imagem.url,
      path: imagem.path || "",
      nomeArquivo: imagem.nome || "",
      origem: ORIGEM_FOTO_AGREGADO,
      agregadoId,
      imagemMaterialId: imagem.id || "",
    }];
  });
}

function fotosDoRelatorioComAgregados(dosagem, materiais = []) {
  const existentes = Array.isArray(dosagem?.relatorioFotografico)
    ? dosagem.relatorioFotografico
    : [];
  const possuiFotosSalvas = existentes.some((foto) => Boolean(foto?.url));

  if (possuiFotosSalvas) {
    const automaticas = fotosAutomaticasDosAgregados(materiais);
    return fotosComMinimo(dosagem).map((foto) => {
      if (foto.origem !== ORIGEM_FOTO_AGREGADO) return foto;
      const atual = automaticas.find((item) => item.agregadoId === foto.agregadoId);
      return atual ? { ...foto, ...atual, id: foto.id, legenda: foto.legenda } : foto;
    });
  }

  const automaticas = fotosAutomaticasDosAgregados(materiais);
  const manuais = existentes.filter(
    (foto) => foto?.origem !== ORIGEM_FOTO_AGREGADO
  );

  return fotosComMinimo({
    ...dosagem,
    relatorioFotografico: [...automaticas, ...manuais],
  });
}

function formatarPrensaRuptura(prensa) {
  if (!prensa) return "";
  const partes = [
    prensa.codigo,
    prensa.empresa,
    prensa.equipamento,
    prensa.serie && `Serie ${prensa.serie}`,
    prensa.dataCalibracao && `Calibracao ${formatarDataBR(prensa.dataCalibracao)}`,
  ].filter(Boolean);
  return partes.join(" - ");
}

function normalizarDataValidadeISO(valor) {
  if (!valor) return "";

  if (typeof valor === "string") {
    if (/^\d{4}-\d{2}-\d{2}/.test(valor)) return valor.slice(0, 10);
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(valor)) {
      const [dia, mes, ano] = valor.split("/");
      return `${ano}-${mes}-${dia}`;
    }
  }

  const data = valor?.toDate ? valor.toDate() : valor instanceof Date ? valor : null;
  if (!data || Number.isNaN(data.getTime())) return "";

  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, "0");
  const dia = String(data.getDate()).padStart(2, "0");
  return `${ano}-${mes}-${dia}`;
}

function obterDataVencimentoPrensaISO(prensa) {
  const dataInformada = normalizarDataValidadeISO(prensa?.dataVencimento);
  if (dataInformada) return dataInformada;

  const dataCalibracao = normalizarDataValidadeISO(prensa?.dataCalibracao || prensa?.data);
  const textoValidade = String(prensa?.validade || "").trim().toLowerCase().replace(",", ".");
  const quantidade = Number(textoValidade.match(/[\d.]+/)?.[0]);

  if (!dataCalibracao || !Number.isFinite(quantidade) || quantidade <= 0) return "";

  const [ano, mes, dia] = dataCalibracao.split("-").map(Number);
  const vencimento = new Date(ano, mes - 1, dia);

  if (textoValidade.includes("mes")) {
    vencimento.setMonth(vencimento.getMonth() + quantidade);
  } else {
    vencimento.setFullYear(vencimento.getFullYear() + quantidade);
  }

  return normalizarDataValidadeISO(vencimento);
}

function confirmarPrensaCalibradaParaRupturas(prensa, datasRuptura = []) {
  const dataVencimento = obterDataVencimentoPrensaISO(prensa);
  if (!dataVencimento) return true;

  const primeiraRupturaAposValidade = datasRuptura
    .map(normalizarDataValidadeISO)
    .filter((data) => data && data > dataVencimento)
    .sort()[0];

  if (!primeiraRupturaAposValidade) return true;

  return window.confirm(
    `A prensa que está utilizando não está calibrada para a ruptura de ${formatarDataBR(primeiraRupturaAposValidade)}. ` +
    `O certificado venceu em ${formatarDataBR(dataVencimento)}. Deseja continuar?`
  );
}

function adicionarDiasDataBR(dataBase, dias) {
  const quantidadeDias = numero(dias);
  if (!dataBase || quantidadeDias === null) return "-";

  let data;
  if (typeof dataBase === "string" && dataBase.includes("-")) {
    const [ano, mes, dia] = dataBase.split("-").map(Number);
    data = new Date(ano, mes - 1, dia);
  } else if (dataBase?.seconds) {
    data = new Date(dataBase.seconds * 1000);
  } else if (dataBase?.toDate) {
    data = dataBase.toDate();
  } else {
    data = new Date(dataBase);
  }

  if (Number.isNaN(data.getTime())) return "-";
  data.setDate(data.getDate() + quantidadeDias);
  return data.toLocaleDateString("pt-BR");
}

function arredondarCincoGramas(valor) {
  const n = typeof valor === "number" ? valor : numero(valor);
  if (n === null) return null;
  return Math.round(n / 5) * 5;
}

function obterNumeroCaracterizacao(material, campos = []) {
  for (const campo of campos) {
    const valor = material?.[campo];
    const n = numero(valor);
    if (n !== null) return n;
  }

  return null;
}

function obterModuloFinura(material) {
  return obterNumeroCaracterizacao(material, [
    "moduloFinura",
    "moduloDeFinura",
    "mf",
    "ModuloFinura",
    "modulo_finura",
  ]);
}

function obterDiametroMaximo(material) {
  return obterNumeroCaracterizacao(material, [
    "diametroMaximo",
    "diferencaMaxima",
    "diametro_maximo",
    "dmc",
    "DMC",
    "DiametroMaximo",
  ]);
}

function calcularFcj(fck, sd) {
  const fckNum = numeroInicial(fck);
  const sdNum = numeroInicial(sd);

  if (fckNum === null || sdNum === null) return null;

  return fckNum + 1.65 * sdNum;
}

function sugerirFatorAc(fcj) {
  const resistencia = typeof fcj === "number" ? fcj : numero(fcj);
  if (resistencia === null) return null;

  if (resistencia <= 20) return 0.65;
  if (resistencia <= 25) return 0.58;
  if (resistencia <= 30) return 0.53;
  if (resistencia <= 35) return 0.49;
  if (resistencia <= 40) return 0.45;
  if (resistencia <= 45) return 0.42;
  if (resistencia <= 50) return 0.39;

  return 0.36;
}

function sugerirAguaInicial(slump, diametroMaximo, usaAditivo = false) {
  const slumpNum = numero(slump);
  const dmc = numero(diametroMaximo);

  if (slumpNum === null) return null;

  let aguaBase = 180;

  if (dmc !== null) {
    if (dmc >= 25) aguaBase = 170;
    else if (dmc >= 19) aguaBase = 178;
    else if (dmc >= 12.5) aguaBase = 188;
    else if (dmc >= 9.5) aguaBase = 198;
  }

  if (slumpNum <= 80) aguaBase -= 8;
  else if (slumpNum <= 120) aguaBase += 0;
  else if (slumpNum <= 160) aguaBase += 8;
  else if (slumpNum <= 200) aguaBase += 16;
  else aguaBase += 22;

  if (usaAditivo) aguaBase *= 0.94;

  return Math.round(aguaBase * 10) / 10;
}

function sugerirTeorArgamassa(moduloFinuraAreia) {
  const mf = numero(moduloFinuraAreia);

  if (mf === null) return 54;

  if (mf < 2.2) return 51;
  if (mf < 2.6) return 53;
  if (mf < 3.0) return 55;

  return 57;
}

function limitarNumero(valor, minimo, maximo) {
  const n = typeof valor === "number" ? valor : numero(valor);
  if (n === null) return null;

  return Math.min(maximo, Math.max(minimo, n));
}

function sugerirPercentualAreia(moduloFinuraAreia, diametroMaximoGraudo, teorArgamassa) {
  const mf = numero(moduloFinuraAreia);
  const dmc = numero(diametroMaximoGraudo);
  const teor = numero(teorArgamassa) ?? 54;

  let areia = 46;

  if (mf !== null) {
    if (mf < 2.2) areia -= 3;
    else if (mf < 2.6) areia += 0;
    else if (mf < 3.0) areia += 2;
    else areia += 4;
  }

  if (dmc !== null) {
    if (dmc >= 25) areia -= 2;
    else if (dmc <= 12.5) areia += 2;
  }

  areia += (teor - 54) * 0.35;

  return limitarNumero(areia, 38, 55);
}

function formatarNumeroCampo(valor, casas = 1) {
  const n = typeof valor === "number" ? valor : numero(valor);
  if (n === null) return "";

  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

function obterVolumeCpLitros(tipoCp) {
  const texto = String(tipoCp || "").toLowerCase();

  if (texto.includes("15x30") || texto.includes("15 x 30")) {
    return 5.3;
  }

  if (texto.includes("10x20") || texto.includes("10 x 20")) {
    return 1.57;
  }

  return 1.57;
}

function sugerirVolumeBetoneira(cps, tipoCp) {
  const quantidade = numero(cps);
  if (quantidade === null || quantidade <= 0) return "";

  const volumeCp = obterVolumeCpLitros(tipoCp);
  const volume = quantidade * volumeCp * 1.25;

  if (volume <= 6) return "6";
  if (volume <= 8) return "8";
  if (volume <= 10) return "10";
  if (volume <= 12) return "12";
  if (volume <= 15) return "15";
  if (volume <= 18) return "18";

  return String(Math.ceil(volume));
}

function CampoNumerico({ value, onChange, campo, linha, coluna, ...props }) {
  function handleChange(event) {
    const valor = event.target.value.replace(/[^\d,.]/g, "");
    onChange(valor);
  }

  function handleKeyDown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      const seletor = `[data-numero-coluna='${coluna}'][data-numero-linha='${linha + 1}'] input`;
      document.querySelector(seletor)?.focus();
      return;
    }

    const teclasPermitidas = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Tab",
      "Home",
      "End",
    ];

    if (teclasPermitidas.includes(event.key) || event.ctrlKey || event.metaKey) return;
    if (!/[\d,.]/.test(event.key)) event.preventDefault();
  }

  return (
    <TextField
      {...props}
      variant="standard"
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      inputProps={{
        ...(props.inputProps || {}),
        inputMode: "decimal",
      }}
      data-numero-campo={campo}
      data-numero-linha={linha}
      data-numero-coluna={coluna}
    />
  );
}

function irParaCampoDosagem(event, proximoCampo) {
  if (event.key !== "Enter") return;
  event.preventDefault();
  if (!proximoCampo) return;
  document.querySelector(`[data-dosagem-campo='${proximoCampo}']`)?.focus();
}

function navegarEntreParcialEProporcao(event) {
  if (event.key !== "Enter") return;
  event.preventDefault();

  const campoAtual = event.target;
  const colunaAtual = campoAtual.dataset.dosagemColuna;
  if (!colunaAtual) return;

  const camposEditaveis = (coluna) =>
    Array.from(document.querySelectorAll(`[data-dosagem-coluna='${coluna}']`))
      .filter((campo) => !campo.disabled && !campo.readOnly && campo.offsetParent !== null);

  const camposDaColuna = camposEditaveis(colunaAtual);
  const posicaoAtual = camposDaColuna.indexOf(campoAtual);

  if (posicaoAtual >= 0 && posicaoAtual < camposDaColuna.length - 1) {
    camposDaColuna[posicaoAtual + 1].focus();
    return;
  }

  const proximaColuna = colunaAtual === "parcial" ? "proporcao" : "parcial";
  camposEditaveis(proximaColuna)[0]?.focus();
}


function recalcularCimentoAutomatico(dosagemAtual, materiais) {
  const fatorAc = numero(dosagemAtual.fatorAc);
  const teorAr = numero(dosagemAtual.teorArConsiderado) || 0;
  const densidadeCimento = numero(dosagemAtual.cimentoDensidade || "3,100") || 3.1;

  if (!fatorAc) return dosagemAtual;

  const agregados = materiais || [];
  if (!agregados.length) return dosagemAtual;

  const percentualTotalAgregados = agregados.reduce((total, material, index) => {
    const proporcao = numero(dosagemAtual[`materialProporcaoGrupo${index}`]) || 100;
    const parcial = numero(dosagemAtual[`materialParcial${index}`]) || 0;
    return total + (proporcao / 100) * (parcial / 100);
  }, 0);

  if (!percentualTotalAgregados) return dosagemAtual;

  const volumeAr = teorAr * 10;

  const massaEspecificaPonderada = agregados.reduce((total, material, index) => {
    const proporcao = numero(dosagemAtual[`materialProporcaoGrupo${index}`]) || 100;
    const parcial = numero(dosagemAtual[`materialParcial${index}`]) || 0;
    const participacao = (proporcao / 100) * (parcial / 100);
    const densidade = numero(material.densidade) || 0;

    return total + participacao * densidade;
  }, 0);

  if (!massaEspecificaPonderada) return dosagemAtual;

  const teorArgamassaDesejado =
    numero(dosagemAtual.teorArgamassaInicial) ||
    numero(dosagemAtual.teorArgamassaConsiderado) ||
    54;

  let melhorCimento = null;
  let menorErro = Infinity;

  for (let cimento = 250; cimento <= 600; cimento += 1) {
    const agua = cimento * fatorAc;
    const volumeCimento = cimento / densidadeCimento;
    const volumeAgua = agua;

    const volumeNaoAgregado = volumeCimento + volumeAgua + volumeAr;
    const volumeAgregado = Math.max(0, 1000 - volumeNaoAgregado);
    const massaAgregados = volumeAgregado * massaEspecificaPonderada;

    let massaMiudos = 0;
    let massaGraudos = 0;

    agregados.forEach((material, index) => {
      const proporcao = numero(dosagemAtual[`materialProporcaoGrupo${index}`]) || 100;
      const parcial = numero(dosagemAtual[`materialParcial${index}`]) || 0;
      const participacao = (proporcao / 100) * (parcial / 100);
      const massa = massaAgregados * participacao;

      if (material.tipoAgregado === "miudo") massaMiudos += massa;
      if (material.tipoAgregado === "graudo") massaGraudos += massa;
    });

    const massaArgamassa = cimento + massaMiudos;
    const massaTotal = cimento + massaMiudos + massaGraudos;

    if (!massaTotal) continue;

    const teorArgamassaCalculado = (massaArgamassa / massaTotal) * 100;
    const erro = Math.abs(teorArgamassaCalculado - teorArgamassaDesejado);

    if (erro < menorErro) {
      menorErro = erro;
      melhorCimento = cimento;
    }
  }

  if (!melhorCimento) return dosagemAtual;

  return {
    ...dosagemAtual,
    cimentoPeso: formatarNumeroCampo(melhorCimento, 0),
  };
}

function parametrosRecalculoIguaisAoOriginal(atual = {}, original = {}) {
  const camposFixos = [
    "fatorAc",
    "teorArConsiderado",
    "teorArgamassaInicial",
  ];
  const camposDinamicos = Array.from(
    new Set([...Object.keys(atual), ...Object.keys(original)])
  ).filter(
    (campo) =>
      campo.startsWith("materialParcial") ||
      campo.startsWith("materialProporcaoGrupo")
  );

  return [...camposFixos, ...camposDinamicos].every((campo) => {
    const valorAtual = numero(atual[campo]);
    const valorOriginal = numero(original[campo]);

    if (valorAtual === null && valorOriginal === null) return true;
    return valorAtual === valorOriginal;
  });
}

function areaCpDosagem(dosagem = {}) {
  const diametroCp = numero(dosagem.diametroCp);
  if (diametroCp && diametroCp > 0) {
    const raioCm = diametroCp / 20;
    return Math.PI * raioCm * raioCm;
  }

  const areaInformada = numero(dosagem.areaCp || dosagem.tipoAreaCp);
  if (areaInformada) return areaInformada;

  const tipo = String(dosagem.tipoCp || dosagem.tipoCorpoProva || "").toLowerCase();

  if (tipo.includes("15x30") || tipo.includes("15 x 30")) return 176.71;
  return 78.54; // CP 10x20
}

function toneladaParaMpa(cargaTon, dosagem = {}) {
  const carga = numero(cargaTon);
  const area = areaCpDosagem(dosagem);

  if (carga === null || !area) return "";

  return numeroBR((carga * 98.0665) / area, 2);
}

function valorRupturaTon(dosagem = {}, cp, idade) {
  return dosagem[`rupturaCp${cp}TonIdade${idade.index}`] || "";
}

function possuiResultadosRuptura(dosagem = {}) {
  return Object.entries(dosagem).some(
    ([campo, valor]) =>
      campo.startsWith("rupturaCp") && String(valor ?? "").trim() !== ""
  );
}


export default function DetalhesDosagem() {
  const { dosagemId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { podeEditarDosagem } = usePermissoesAcesso();
  const somenteLeitura = !podeEditarDosagem;
  const areaRecorteFotoRef = useRef(null);
  const arrasteFotoRef = useRef(null);
  const [dosagem, setDosagem] = useState(null);
  const [dosagemOriginal, setDosagemOriginal] = useState(null);
  const [materiais, setMateriais] = useState([]);
  const [calibracoesPrensa, setCalibracoesPrensa] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [modoPrint, setModoPrint] = useState(false);
  const [modoPrintGuia, setModoPrintGuia] = useState(false);
  const [modoPrintInformacoesTraco, setModoPrintInformacoesTraco] = useState(false);
  const [htmlInformacoesTraco, setHtmlInformacoesTraco] = useState("");
  const [temAlteracoes, setTemAlteracoes] = useState(false);
  const [openFotos, setOpenFotos] = useState(false);
  const [uploadingFotoIndex, setUploadingFotoIndex] = useState(null);
  const [fotosDraft, setFotosDraft] = useState([]);
  const [recorteFoto, setRecorteFoto] = useState({
    open: false,
    index: null,
    url: "",
    zoom: 1,
    deslocamentoX: 0,
    deslocamentoY: 0,
    larguraImagem: 0,
    alturaImagem: 0,
  });
  const [openConfirmarCancelamento, setOpenConfirmarCancelamento] = useState(false);
  const [preDimensionamentoVisivel, setPreDimensionamentoVisivel] = useState(false);
  const [dosagemVolumeVisivel, setDosagemVolumeVisivel] = useState(false);

  useEffect(() => {
    carregarDosagem();
    carregarCalibracoesPrensa();
  }, [dosagemId]);

  async function carregarCalibracoesPrensa() {
    const q = query(collection(db, "tblCalibracaoPrensas"), orderBy("dataCalibracao", "desc"));
    const snap = await getDocs(consultaTenantAtual(q));
    setCalibracoesPrensa(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
  }

  const idDosagemCarregada = dosagem?.id;
  useEffect(() => {
    if (idDosagemCarregada && new URLSearchParams(location.search).get("print") === "1") {
      setModoPrint(true);
    }
  }, [idDosagemCarregada, location.search]);

  async function buscarCaracterizacaoAgregado(resumo = {}, idPreferencial = "") {
    if (idPreferencial) {
      const snapDireto = await getDoc(doc(db, "tblCaracterizacaoAgregados", idPreferencial));
      if (snapDireto.exists()) {
        return { id: snapDireto.id, ...snapDireto.data() };
      }
    }

    const materialCadastroId =
      resumo.materialCadastroId ||
      resumo.fornecedorId ||
      resumo.cadastroMaterialId ||
      idPreferencial ||
      "";

    if (materialCadastroId) {
      for (const campo of ["fornecedorId", "materialCadastroId"]) {
        const q = query(
          collection(db, "tblCaracterizacaoAgregados"),
          where(campo, "==", materialCadastroId),
          limit(1)
        );
        const snap = await getDocs(consultaTenantAtual(q));
        if (!snap.empty) {
          const item = snap.docs[0];
          return { id: item.id, ...item.data() };
        }
      }
    }

    const codigo = codigoMaterial(resumo);
    if (codigo) {
      for (const campo of ["codigoFornecedor", "fornecedorCodigo", "codigoMaterial", "codigo"]) {
        const q = query(
          collection(db, "tblCaracterizacaoAgregados"),
          where(campo, "==", codigo),
          limit(1)
        );
        const snap = await getDocs(consultaTenantAtual(q));
        if (!snap.empty) {
          const item = snap.docs[0];
          return { id: item.id, ...item.data() };
        }
      }
    }

    return null;
  }

  async function atualizarMassasUnitariasPadiolas() {
    const agregadosAtualizados = await Promise.all(
      materiais.map(async (material) => {
        const caracterizacao = await buscarCaracterizacaoAgregado(
          material.caracterizacaoCompleta || material,
          material.id
        );

        return caracterizacao
          ? materialLinha({
              ...material,
              ...caracterizacao,
              materialCadastroId:
                caracterizacao.fornecedorId ||
                caracterizacao.materialCadastroId ||
                material.materialCadastroId ||
                "",
            })
          : material;
      })
    );

    setMateriais(agregadosAtualizados);

    const agregadoSemMassaUnitaria = agregadosAtualizados.find(
      (material) => numero(material.massaUnitaria) === null
    );

    if (agregadoSemMassaUnitaria) {
      const nomeAgregado =
        agregadoSemMassaUnitaria.material ||
        agregadoSemMassaUnitaria.descricao ||
        agregadoSemMassaUnitaria.amostra ||
        "informado";

      alert(`O agregado ${nomeAgregado} não foi realizado o ensaio de massa unitária.`);
      return false;
    }

    return true;
  }

  async function carregarDosagem() {
  const snap = await getDoc(doc(db, "tblDosagensConcreto", dosagemId));
  if (!snap.exists()) return;

  const dadosFirestore = snap.data();
  const fcjInicial = calcularFcj(dadosFirestore.fck, dadosFirestore.sd);
  const dados = {
    id: snap.id,
    ...dadosFirestore,
    fckest:
      fcjInicial !== null
        ? formatarNumeroCampo(fcjInicial, 1)
        : dadosFirestore.fckest || "",
    numeroRevisao: obterNumeroRevisao(dadosFirestore),
    tipoGuiaDosagem: dadosFirestore.tipoGuiaDosagem || "acumulada",
    teorArConsiderado: dadosFirestore.teorArConsiderado || "",
    teorArEncontrado: dadosFirestore.teorArEncontrado || "",
    massaEspecificaObtida: dadosFirestore.massaEspecificaObtida || "",
    volumeBetoneira: dadosFirestore.volumeBetoneira || "",
  };

  setDosagemOriginal(dados);
  setTemAlteracoes(false);

  const materiaisResumo = Array.isArray(dados.materiaisResumo)
    ? dados.materiaisResumo
        .map((item, index) => ({ ...item, ordemOriginal: index }))
        .sort(
          (a, b) =>
            Number(a.ordemSelecao ?? a.ordemOriginal) -
            Number(b.ordemSelecao ?? b.ordemOriginal)
        )
    : [];

  const ids = Array.isArray(dados.materialIds) ? dados.materialIds : [];

  const materiaisCompletos = await Promise.all(
    materiaisResumo.map(async (resumo, index) => {
      const id = resumo.id || resumo.materialId || ids[index];

      if (!id) return materialLinha(resumo);

      const caracterizacaoEncontrada = await buscarCaracterizacaoAgregado(resumo, id);

      if (!caracterizacaoEncontrada) {
        return materialLinha(resumo);
      }

      const caracterizacao = {
        ...resumo,
        ...caracterizacaoEncontrada,
      };
      const materialCadastroId =
        caracterizacao.fornecedorId ||
        caracterizacao.materialCadastroId ||
        resumo.materialCadastroId;
      const materialCadastroSnap = materialCadastroId
        ? await getDoc(
            doc(db, "tblListaMateriaisCadastrados", materialCadastroId)
          )
        : null;

      const materialCadastro = materialCadastroSnap?.exists()
  ? materialCadastroSnap.data()
  : {};

return materialLinha({
  ...caracterizacao,
  materialCadastroId,
  ImagensMaterial: normalizarImagensDoMaterial(materialCadastro),
  descricao:
    materialCadastro.Descricao ||
    materialCadastro.descricao ||
    caracterizacao.descricao ||
    caracterizacao.Descricao ||
    caracterizacao.amostra,
  procedencia:
    materialCadastro.Fornecedor ||
    caracterizacao.procedencia ||
    caracterizacao.fornecedor ||
    caracterizacao.Fornecedor ||
    "",
  codigo: codigoMaterial(caracterizacao) || codigoMaterial(materialCadastro),
});
    })
  );

  if (materiaisCompletos.length > 0) {
    setMateriais(materiaisCompletos);
    setDosagem(dados);
    return;
  }

  const materiaisSnap = await Promise.all(
    ids.map(async (id) => {
      const caracterizacao = await buscarCaracterizacaoAgregado({ id }, id);
      if (!caracterizacao) return null;
      const materialCadastroId =
        caracterizacao.fornecedorId ||
        caracterizacao.materialCadastroId;
      const materialCadastroSnap = materialCadastroId
        ? await getDoc(
            doc(db, "tblListaMateriaisCadastrados", materialCadastroId)
          )
        : null;

      const materialCadastro = materialCadastroSnap?.exists()
        ? materialCadastroSnap.data()
        : {};

      return {
        ...caracterizacao,
        materialCadastroId,
        ImagensMaterial: normalizarImagensDoMaterial(materialCadastro),
        descricao:
          materialCadastro.Descricao ||
          materialCadastro.descricao ||
          caracterizacao.descricao ||
          caracterizacao.Descricao ||
          caracterizacao.amostra,
        procedencia:
          materialCadastro.Fornecedor ||
          caracterizacao.procedencia ||
          caracterizacao.fornecedor ||
          caracterizacao.Fornecedor ||
          "",
        codigo: codigoMaterial(caracterizacao) || codigoMaterial(materialCadastro),
      };
    })
  );

  setMateriais(materiaisSnap.filter(Boolean).map(materialLinha));
  setDosagem(dados);
}

function atualizarCampo(campo, valor) {
  if (somenteLeitura) return;

  setDosagem((prev) => {
    const atualizado = {
      ...prev,
      [campo]: valor,
    };

    if (campo === "fck" || campo === "sd") {
      const fck = campo === "fck" ? numeroInicial(valor) : numeroInicial(atualizado.fck);
      const sd = campo === "sd" ? numeroInicial(valor) : numeroInicial(atualizado.sd);
      const fcj = calcularFcj(fck, sd);

      atualizado.fckest = fcj !== null ? formatarNumeroCampo(fcj, 1) : "";
    }

    return atualizado;
  });

  setTemAlteracoes(true);
}

function atualizarProporcaoAgregado(index, valor) {
  if (somenteLeitura) return;

  setDosagem((prev) => {
    const atualizado = {
      ...prev,
      [`materialProporcaoGrupo${index}`]: valor,
    };
    const material = materiaisClassificados.agregados[index];
    const percentual = /\d/.test(String(valor)) ? numeroInicial(valor) : null;

    if (!material || percentual === null || percentual < 0 || percentual > 100) {
      return atualizado;
    }

    const indicesMesmoTipo = materiaisClassificados.agregados
      .map((item, indice) => (item.tipoAgregado === material.tipoAgregado ? indice : -1))
      .filter((indice) => indice >= 0);

    if (indicesMesmoTipo.length !== 2) return atualizado;

    const outroIndex = indicesMesmoTipo.find((indice) => indice !== index);
    const textoValor = String(valor);
    const separadorDecimal = textoValor.includes(",") ? "," : textoValor.includes(".") ? "." : null;
    const casasDecimais = separadorDecimal
      ? Math.min(textoValor.split(separadorDecimal)[1]?.length || 0, 3)
      : 0;

    atualizado[`materialProporcaoGrupo${outroIndex}`] = formatarNumeroCampo(
      100 - percentual,
      casasDecimais
    );

    return atualizado;
  });

  setTemAlteracoes(true);
}

function atualizarObservacaoImportante(index, campo, valor) {
  if (somenteLeitura) return;

  setDosagem((prev) => {
    const lista = observacoesImportantesDosagem(prev).map((item) => ({ ...item }));
    lista[index] = {
      ...(lista[index] || { titulo: "", texto: "" }),
      [campo]: valor,
      ...(campo === "texto" && index < 2 ? { textoManual: true } : {}),
    };

    return {
      ...prev,
      observacoesImportantes: lista,
    };
  });

  setTemAlteracoes(true);
}

function adicionarObservacaoImportante() {
  if (somenteLeitura) return;

  setDosagem((prev) => ({
    ...prev,
    observacoesImportantes: [
      ...observacoesImportantesDosagem(prev),
      { titulo: "", texto: "" },
    ],
  }));

  setTemAlteracoes(true);
}

function removerObservacaoImportante(index) {
  if (somenteLeitura) return;

  setDosagem((prev) => {
    const lista = observacoesImportantesDosagem(prev).filter((_, itemIndex) => itemIndex !== index);

    return {
      ...prev,
      observacoesImportantes: lista.length ? lista : [{ titulo: "", texto: "" }],
    };
  });

  setTemAlteracoes(true);
}

function atualizarMetodologia(index, valor) {
  if (somenteLeitura) return;

  setDosagem((prev) => {
    const lista = [...metodologiasDosagem(prev)];
    lista[index] = valor;

    return {
      ...prev,
      metodologiasRelatorio: lista,
    };
  });

  setTemAlteracoes(true);
}

function adicionarMetodologia() {
  if (somenteLeitura) return;

  setDosagem((prev) => ({
    ...prev,
    metodologiasRelatorio: [...metodologiasDosagem(prev), ""],
  }));

  setTemAlteracoes(true);
}

function removerMetodologia(index) {
  if (somenteLeitura) return;

  setDosagem((prev) => {
    const lista = metodologiasDosagem(prev).filter((_, itemIndex) => itemIndex !== index);

    return {
      ...prev,
      metodologiasRelatorio: lista.length ? lista : [""],
    };
  });

  setTemAlteracoes(true);
}

  async function abrirCadastroFotos() {
    if (somenteLeitura) return;

    const fotos = fotosDoRelatorioComAgregados(dosagem, materiais).map((foto) => ({ ...foto }));
    setFotosDraft(fotos);
    setOpenFotos(true);
    setUploadingFotoIndex(-1);
    try {
      const atualizadas = await Promise.all(fotos.map(async (foto) => {
        if (foto.origem !== ORIGEM_FOTO_AGREGADO || (!foto.path && !foto.url)) return foto;
        try {
          const url = await getDownloadURL(ref(storage, foto.path || foto.url));
          return { ...foto, url };
        } catch (error) {
          console.warn("Não foi possível atualizar a imagem do agregado", error);
          return foto;
        }
      }));
      setFotosDraft(atualizadas);
    } finally {
      setUploadingFotoIndex(null);
    }
  }

  function adicionarFotoExtra() {
    if (somenteLeitura) return;

    setFotosDraft((prev) => [
      ...prev,
      {
        id: `foto-${Date.now()}`,
        legenda: `FOTO ${String(prev.length + 1).padStart(2, "0")}`,
        url: "",
        path: "",
        nomeArquivo: "",
      },
    ]);
  }

  function selecionarImagemFoto(index, arquivo) {
    if (somenteLeitura) return;
    if (!arquivo) return;

    setRecorteFoto({
      open: true,
      index,
      url: URL.createObjectURL(arquivo),
      zoom: 1,
      deslocamentoX: 0,
      deslocamentoY: 0,
      larguraImagem: 0,
      alturaImagem: 0,
    });
  }

  function fecharRecorteFoto() {
    if (recorteFoto.url) URL.revokeObjectURL(recorteFoto.url);
    setRecorteFoto({
      open: false,
      index: null,
      url: "",
      zoom: 1,
      deslocamentoX: 0,
      deslocamentoY: 0,
      larguraImagem: 0,
      alturaImagem: 0,
    });
  }

  async function confirmarRecorteFoto() {
    if (somenteLeitura) return;

    try {
      const arquivo = await gerarFotoDosagemRecortada(recorteFoto);
      const previewUrl = URL.createObjectURL(arquivo);

      setFotosDraft((prev) =>
        prev.map((foto, itemIndex) => {
          if (itemIndex !== recorteFoto.index) return foto;
          if (foto.previewUrl) URL.revokeObjectURL(foto.previewUrl);
          return {
            ...foto,
            arquivo,
            previewUrl,
            erroImagem: false,
            nomeArquivo: arquivo.name,
          };
        })
      );
      fecharRecorteFoto();
    } catch (error) {
      alert(error.message);
    }
  }

  function calcularLayoutRecorteFoto() {
    const area = areaRecorteFotoRef.current;
    if (!area || !recorteFoto.larguraImagem || !recorteFoto.alturaImagem) {
      return null;
    }

    const larguraArea = area.clientWidth;
    const alturaArea = area.clientHeight;
    const escalaBase = Math.max(
      larguraArea / recorteFoto.larguraImagem,
      alturaArea / recorteFoto.alturaImagem
    );
    const escala = escalaBase * recorteFoto.zoom;
    const larguraImagem = recorteFoto.larguraImagem * escala;
    const alturaImagem = recorteFoto.alturaImagem * escala;

    return {
      larguraImagem,
      alturaImagem,
      limiteX: Math.max(0, (larguraImagem - larguraArea) / 2),
      limiteY: Math.max(0, (alturaImagem - alturaArea) / 2),
    };
  }

  function estiloImagemRecorteFoto() {
    const layout = calcularLayoutRecorteFoto();
    if (!layout) return { visibility: "hidden" };

    return {
      position: "absolute",
      top: "50%",
      left: "50%",
      width: layout.larguraImagem,
      height: layout.alturaImagem,
      maxWidth: "none",
      transform: `translate(-50%, -50%) translate(${recorteFoto.deslocamentoX * layout.limiteX}px, ${recorteFoto.deslocamentoY * layout.limiteY}px)`,
      userSelect: "none",
      pointerEvents: "none",
    };
  }

  function iniciarArrasteFoto(event) {
    const layout = calcularLayoutRecorteFoto();
    if (!layout) return;

    event.currentTarget.setPointerCapture(event.pointerId);
    arrasteFotoRef.current = {
      pointerId: event.pointerId,
      inicioX: event.clientX,
      inicioY: event.clientY,
      deslocamentoX: recorteFoto.deslocamentoX,
      deslocamentoY: recorteFoto.deslocamentoY,
      limiteX: layout.limiteX,
      limiteY: layout.limiteY,
    };
  }

  function moverFotoRecorte(event) {
    const arraste = arrasteFotoRef.current;
    if (!arraste || arraste.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - arraste.inicioX;
    const deltaY = event.clientY - arraste.inicioY;
    const proximoX = arraste.limiteX
      ? arraste.deslocamentoX + deltaX / arraste.limiteX
      : 0;
    const proximoY = arraste.limiteY
      ? arraste.deslocamentoY + deltaY / arraste.limiteY
      : 0;

    setRecorteFoto((prev) => ({
      ...prev,
      deslocamentoX: Math.max(-1, Math.min(1, proximoX)),
      deslocamentoY: Math.max(-1, Math.min(1, proximoY)),
    }));
  }

  function finalizarArrasteFoto(event) {
    if (arrasteFotoRef.current?.pointerId === event.pointerId) {
      arrasteFotoRef.current = null;
    }
  }

  function atualizarLegendaFoto(index, legenda) {
    if (somenteLeitura) return;

    setFotosDraft((prev) =>
      prev.map((foto, itemIndex) => (itemIndex === index ? { ...foto, legenda } : foto))
    );
  }

  function removerImagemFoto(index) {
    if (somenteLeitura) return;

    setFotosDraft((prev) =>
      prev.map((foto, itemIndex) =>
        itemIndex === index
          ? (() => {
              if (foto.previewUrl) URL.revokeObjectURL(foto.previewUrl);
              return {
                ...foto,
                url: "",
                path: "",
                arquivo: null,
                previewUrl: "",
                nomeArquivo: "",
                removerPath:
                  foto.origem === ORIGEM_FOTO_AGREGADO
                    ? ""
                    : foto.path || foto.removerPath || "",
              };
            })()
          : foto
      )
    );
  }

  function cancelarFotos() {
    fotosDraft.forEach((foto) => {
      if (foto.previewUrl) URL.revokeObjectURL(foto.previewUrl);
    });
    setFotosDraft([]);
    setOpenFotos(false);
  }

  async function salvarFotos() {
    if (somenteLeitura) {
      alert("Seu acesso permite apenas visualizar dosagens.");
      return;
    }

    if (!dosagem?.id) return;

    setUploadingFotoIndex(-1);
    try {
      const fotosSalvas = [];

      for (let index = 0; index < fotosDraft.length; index++) {
        const foto = fotosDraft[index];
        let fotoFinal = {
          id: foto.id || `foto-${index + 1}`,
          legenda: foto.legenda || `FOTO ${String(index + 1).padStart(2, "0")}`,
          url: foto.url || "",
          path: foto.path || "",
          nomeArquivo: foto.nomeArquivo || "",
          origem: foto.origem || "manual",
          agregadoId: foto.agregadoId || "",
          imagemMaterialId: foto.imagemMaterialId || "",
        };

        if (foto.arquivo) {
          const arquivoComprimido = await compressImage(foto.arquivo, {
            maxWidth: 1400,
            maxHeight: 1400,
            quality: 0.72,
          });
          const caminho = `dosagens/${dosagem.id}/relatorio-fotografico/foto-${index + 1}-${Date.now()}.jpg`;
          const storageRef = ref(storage, caminho);
          await uploadBytes(storageRef, arquivoComprimido, { contentType: "image/jpeg" });
          const url = await getDownloadURL(storageRef);

          if (foto.path && foto.origem !== ORIGEM_FOTO_AGREGADO) {
            deleteObject(ref(storage, foto.path)).catch(() => {});
          }

          fotoFinal = {
            ...fotoFinal,
            url,
            path: caminho,
            nomeArquivo: foto.arquivo.name,
            origem: "manual",
            agregadoId: "",
            imagemMaterialId: "",
          };
        } else if (foto.removerPath) {
          deleteObject(ref(storage, foto.removerPath)).catch(() => {});
        }

        fotosSalvas.push(fotoFinal);
      }

      await updateDoc(doc(db, "tblDosagensConcreto", dosagem.id), {
        relatorioFotografico: fotosSalvas,
        atualizadoEm: new Date(),
      });

      setDosagem((prev) => ({ ...prev, relatorioFotografico: fotosSalvas }));
      setDosagemOriginal((prev) => ({ ...prev, relatorioFotografico: fotosSalvas }));
      setFotosDraft([]);
      setOpenFotos(false);
    } finally {
      setUploadingFotoIndex(null);
    }
  }

  async function salvarDosagem() {
    if (somenteLeitura) {
      alert("Seu acesso permite apenas visualizar dosagens.");
      return;
    }

    if (!dosagem?.id) return;

    if (!String(dosagem.traco || "").trim()) {
      alert("Informe o nome do traço.");
      return;
    }

    if (possuiResultadosRuptura(dosagem) && !dosagem.prensaRuptura?.id) {
      alert("É necessário selecionar uma prensa.");
      return;
    }

    setSalvando(true);
    try {
      const numeroRevisao = obterNumeroRevisao(dosagem);
      const dosagemNormalizada = {
        ...dosagem,
        numeroRevisao,
        traco: normalizarNomeTraco(
          dosagem.codigo,
          numeroRevisao,
          dosagem.traco
        ),
      };
      const { id } = dosagemNormalizada;
      const atualizadoEm = new Date();
      const dadosSalvos = await atualizarDocumentoComMesclagem({
        db,
        referencia: doc(db, "tblDosagensConcreto", id),
        original: dosagemOriginal,
        atual: dosagemNormalizada,
        preparar: (dadosMesclados) => {
          const { id: _id, ...dados } = dadosMesclados;
          const revisaoMesclada = obterNumeroRevisao(dados);
          return {
            ...dados,
            numeroRevisao: revisaoMesclada,
            traco: normalizarNomeTraco(dados.codigo, revisaoMesclada, dados.traco),
          };
        },
        metadados: { atualizadoEm },
      });
      const dosagemSalva = { id, ...dadosSalvos };
      setDosagem(dosagemSalva);
      setDosagemOriginal(dosagemSalva);
      setTemAlteracoes(false);
    } finally {
      setSalvando(false);
    }
  }

  async function confirmarCancelarAlteracoes() {
    if (!dosagem?.id) return;
    setOpenConfirmarCancelamento(false);
    await carregarDosagem();
  }

  function solicitarCancelarAlteracoes() {
    setOpenConfirmarCancelamento(true);
  }

  const materiaisClassificados = useMemo(() => {
    const cimento = dosagem?.cimentoResumo || dosagem?.cimento || { material: "Cimento", descricao: dosagem?.cimentoDescricao || "" };
    const ordenarPorSelecao = (lista = []) =>
      lista
        .map((item, index) => ({ ...item, ordemOriginal: index }))
        .sort(
          (a, b) =>
            Number(a.ordemSelecao ?? a.ordemOriginal) -
            Number(b.ordemSelecao ?? b.ordemOriginal)
        );
    const aditivos = Array.isArray(dosagem?.aditivosResumo)
      ? ordenarPorSelecao(dosagem.aditivosResumo)
      : [];
    const adicoes = Array.isArray(dosagem?.adicoesResumo)
      ? ordenarPorSelecao(dosagem.adicoesResumo)
      : [];
    const agua = dosagem?.aguaResumo || dosagem?.agua || {
      material: "Água",
      descricao: dosagem?.aguaDescricao || "",
      fornecedor: dosagem?.aguaFornecedor || "",
      massaEspecifica: "1,000"
    };
    const agregados = materiais.map((item) => ({
  ...item,
  grupo:
    item.material ||
    item.descricao ||
    item.Descricao ||
    item.amostra ||
    (item.tipoAgregado === "miudo" ? "Areia" : "Brita"),
}));

    return { cimento, aditivos, adicoes, agua, agregados };
  }, [dosagem, materiais]);

  if (!dosagem) return null;

  const fotosRelatorio = fotosDoRelatorioComAgregados(dosagem, materiais);
  const agregadoMiudoPrincipal = materiais.find((item) => item.tipoAgregado === "miudo");
const agregadoGraudoPrincipal = materiais.find((item) => item.tipoAgregado === "graudo");

const moduloFinuraAreia = obterModuloFinura(agregadoMiudoPrincipal);
const diametroMaximoGraudo =
  obterDiametroMaximo(agregadoGraudoPrincipal) ||
  obterDiametroMaximo(materiais.find((item) => obterDiametroMaximo(item) !== null));

const fcjCalculado = calcularFcj(numeroInicial(dosagem.fck),  numeroInicial(dosagem.sd));
const fatorAcSugerido = sugerirFatorAc(fcjCalculado);

const usaAditivo = Array.isArray(dosagem.aditivosResumo)
  ? dosagem.aditivosResumo.length > 0
  : false;

const aguaEstimada = sugerirAguaInicial(
  numeroInicial(dosagem.slump),
  diametroMaximoGraudo,
  usaAditivo
);

const cimentoCalculado =
  aguaEstimada !== null && fatorAcSugerido
    ? aguaEstimada / fatorAcSugerido
    : null;

const teorArgamassaSugerido = sugerirTeorArgamassa(moduloFinuraAreia);

const percentualAreiaSugerido = sugerirPercentualAreia(
  moduloFinuraAreia,
  diametroMaximoGraudo,
  dosagem.teorArgamassaInicial || teorArgamassaSugerido
);

const percentualBritaSugerido =
  percentualAreiaSugerido !== null ? 100 - percentualAreiaSugerido : null;

const volumeBetoneiraSugerido = sugerirVolumeBetoneira(
  dosagem.cps,
  dosagem.tipoCp || dosagem.tipoCorpoProva
);

const observacoesImportantes = observacoesImportantesDosagem(dosagem);
const metodologiasRelatorio = metodologiasDosagem(dosagem);

function aplicarPreDimensionamento() {
  setDosagem((prev) => {
    const atualizado = {
      ...prev,
      fatorAc:
        fatorAcSugerido !== null
          ? formatarNumeroCampo(fatorAcSugerido, 2)
          : prev.fatorAc,
      cimentoPeso:
        cimentoCalculado !== null
          ? formatarNumeroCampo(cimentoCalculado, 0)
          : prev.cimentoPeso,
      teorArConsiderado:
        prev.teorArConsiderado || "1,8",
      teorArgamassaInicial:
        formatarNumeroCampo(teorArgamassaSugerido, 1),
      volumeBetoneira:
        volumeBetoneiraSugerido || prev.volumeBetoneira,
    };

    materiais.forEach((material, index) => {
      atualizado[`materialProporcaoGrupo${index}`] =
        prev[`materialProporcaoGrupo${index}`] || "100";

      if (material.tipoAgregado === "miudo" && percentualAreiaSugerido !== null) {
        atualizado[`materialParcial${index}`] = formatarNumeroCampo(percentualAreiaSugerido, 1);
      }

      if (material.tipoAgregado === "graudo" && percentualBritaSugerido !== null) {
        atualizado[`materialParcial${index}`] = formatarNumeroCampo(percentualBritaSugerido, 1);
      }
    });

    if (Array.isArray(prev.aditivosResumo)) {
      prev.aditivosResumo.forEach((aditivo, index) => {
        const dosagemPadrao =
          aditivo.dosagemPadraoUsual ||
          aditivo.dosagemUsual ||
          aditivo.teorPadrao ||
          aditivo.percentualPadrao;

        if (dosagemPadrao && !prev[`aditivoParcial${index}`]) {
          atualizado[`aditivoParcial${index}`] = String(dosagemPadrao);
        }
      });
    }

    return atualizado;
  });

  setTemAlteracoes(true);
}

  if (modoPrint) {
    return (
      <RelatorioDosagemPrint
        dosagem={dosagem}
        materiais={materiais}
        fotosRelatorio={fotosRelatorio.filter((foto) => foto.url)}
        mostrarDosagemVolume={dosagemVolumeVisivel}
        onVoltar={() => {
          if (location.state?.voltarListaDosagens) {
            history.goBack();
            return;
          }
          setModoPrint(false);
        }}
      />
    );
  }

  if (modoPrintGuia) {
    return (
      <RelatorioGuiaDosagemPrint
        dosagem={dosagem}
        materiais={materiais}
        autoPrint
        onVoltar={() => setModoPrintGuia(false)}
      />
    );
  }

  if (modoPrintInformacoesTraco) {
    return (
      <RelatorioInformacoesTracoExperimentalPrint
        htmlTabela={htmlInformacoesTraco}
        codigo={formatarCodigoDosagem(dosagem.codigo)}
        revisao={obterNumeroRevisao(dosagem)}
        data={formatarDataBR(dosagem.data)}
        dadosTraco={dosagem}
        interessado={dosagem.clienteNome || ""}
        obra={dosagem.obraNome || ""}
        onVoltar={() => setModoPrintInformacoesTraco(false)}
      />
    );
  }

  const adicoesVisiveis = dosagem?.caldaCimento === true
    ? []
    : materiaisClassificados.adicoes.length
      ? materiaisClassificados.adicoes
      : [{}];
  const pesoCimento = numero(dosagem.cimentoPeso) || 0;
  const pesoAdicao = (index) =>
    materialVazio(adicoesVisiveis[index])
      ? 0
      : pesoCimento * ((numero(dosagem[`adicaoParcial${index}`]) || 0) / 100);
  const pesoAdicoes = adicoesVisiveis.reduce((total, _, index) => total + pesoAdicao(index), 0);
  const pesoLigante = pesoCimento + pesoAdicoes;
  const pesoAgua = pesoLigante * (numero(dosagem.fatorAc) || 0);
  const pesoAditivo = (index) => pesoLigante * ((numero(dosagem[`aditivoParcial${index}`]) || 0) / 100);
  const pesoAditivos = materiaisClassificados.aditivos.reduce((total, _, index) => total + pesoAditivo(index), 0);
  const densidadeCimento = numero(dosagem.cimentoDensidade || materiaisClassificados.cimento.massaEspecifica || "3,100") || 1;
  const volumeCimento = pesoCimento / densidadeCimento;
  const volumeAdicao = (index) =>
    materialVazio(adicoesVisiveis[index])
      ? 0
      : pesoAdicao(index) / (numero(adicoesVisiveis[index]?.massaEspecifica) || 1);
  const volumeAdicoes = adicoesVisiveis.reduce((total, _, index) => total + volumeAdicao(index), 0);
  const densidadeAgua = numero(materiaisClassificados.agua.massaEspecifica) || 1;
  const volumeAgua = pesoAgua / densidadeAgua;
  const volumeAditivo = (index) => pesoAditivo(index) / (numero(materiaisClassificados.aditivos[index]?.massaEspecifica) || 1);
  const volumeAditivos = materiaisClassificados.aditivos.reduce((total, _, index) => total + volumeAditivo(index), 0);
  const teorArConsiderado = valorVazio(dosagem.teorArConsiderado)
    ? 0
    : numero(dosagem.teorArConsiderado) || 0;
  const volumeBetoneira = valorVazio(dosagem.volumeBetoneira)
    ? null
    : numero(dosagem.volumeBetoneira);
  const volumeArConsiderado = teorArConsiderado * 10;
  const indicesAgregadosMiudos = materiaisClassificados.agregados
    .map((material, index) => (material.tipoAgregado === "miudo" ? index : -1))
    .filter((index) => index >= 0);
  const indicesAgregadosGraudos = materiaisClassificados.agregados
    .map((material, index) => (material.tipoAgregado === "graudo" ? index : -1))
    .filter((index) => index >= 0);
  const indiceAgregadoMiudo = indicesAgregadosMiudos[0] ?? -1;
  const percentualMiudo =
    indiceAgregadoMiudo >= 0
      ? numero(dosagem[`materialParcial${indiceAgregadoMiudo}`]) || 0
      : 0;
  const percentualGraudo =
    indicesAgregadosGraudos.length > 0
      ? Math.max(0, 100 - percentualMiudo)
      : 0;
  const percentualGrupoAgregado = (material) =>
    material.tipoAgregado === "graudo" ? percentualGraudo : percentualMiudo;
  const somaProporcoesGrupo = (tipoAgregado) => materiaisClassificados.agregados.reduce(
    (total, material, index) => material.tipoAgregado === tipoAgregado
      ? total + (numero(dosagem[`materialProporcaoGrupo${index}`]) || 0)
      : total,
    0
  );
  const percentualParcialAgregado = (material, index) => {
    const somaProporcoes = somaProporcoesGrupo(material.tipoAgregado);
    const proporcao = numero(dosagem[`materialProporcaoGrupo${index}`]) || 0;
    return somaProporcoes > 0
      ? percentualGrupoAgregado(material) * (proporcao / somaProporcoes)
      : 0;
  };
  const percentualAgregadoTotal = (index) =>
    percentualParcialAgregado(materiaisClassificados.agregados[index], index) / 100;
  const massaEspecificaPonderadaAgregados = materiaisClassificados.agregados.reduce(
    (total, material, index) => total + percentualAgregadoTotal(index) * (numero(material.densidade) || 0),
    0
  );
  const volumeArgamassaExperimental =
    volumeCimento + volumeAdicoes + volumeAgua + volumeAditivos + volumeArConsiderado;
  const volumeAgregadosExperimental = Math.max(0, 1000 - volumeArgamassaExperimental);
  const somaParticipacaoAgregados = materiaisClassificados.agregados.reduce(
    (total, _, index) => total + percentualAgregadoTotal(index),
    0
  );
  const divisorVolumeAgregados = materiaisClassificados.agregados.reduce(
    (total, material, index) => {
      const participacao =
        somaParticipacaoAgregados > 0
          ? percentualAgregadoTotal(index) / somaParticipacaoAgregados
          : 0;
      return total + participacao / (numero(material.densidade) || 1);
    },
    0
  );
  const massaAgregadosExperimental =
    divisorVolumeAgregados > 0
      ? volumeAgregadosExperimental / divisorVolumeAgregados
      : 0;
  const pesoAgregado = (index) => {
    const participacao =
      somaParticipacaoAgregados > 0
        ? percentualAgregadoTotal(index) / somaParticipacaoAgregados
        : 0;
    return massaAgregadosExperimental * participacao;
  };
  const volumeAgregado = (index) =>
    pesoAgregado(index) / (numero(materiaisClassificados.agregados[index]?.densidade) || 1);
  const volumeAgregadosMiudos = materiaisClassificados.agregados.reduce(
    (total, material, index) => total + (material.tipoAgregado === "miudo" ? volumeAgregado(index) : 0),
    0
  );
  const pesoAgregadosMiudos = materiaisClassificados.agregados.reduce(
    (total, material, index) => total + (material.tipoAgregado === "miudo" ? pesoAgregado(index) : 0),
    0
  );
  const pesoAgregadosGraudos = materiaisClassificados.agregados.reduce(
    (total, material, index) => total + (material.tipoAgregado === "graudo" ? pesoAgregado(index) : 0),
    0
  );
  const massaArgamassaConsiderada = pesoLigante + pesoAgregadosMiudos;
  const teorArgamassaCalculado =
    massaArgamassaConsiderada + pesoAgregadosGraudos
      ? (massaArgamassaConsiderada / (massaArgamassaConsiderada + pesoAgregadosGraudos)) * 100
      : null;
  const volumeArgamassaConsiderado =
    volumeCimento + volumeAdicoes + volumeAgua + volumeAditivos + volumeArConsiderado;
  const somaPesos = pesoLigante + pesoAgua + pesoAditivos +
    materiaisClassificados.agregados.reduce((total, _, index) => total + pesoAgregado(index), 0);
  const percentualTotal = (peso) => somaPesos ? (peso / somaPesos) * 100 : 0;
  const consideradoBetoneira = (peso) => volumeBetoneira === null ? null : peso * volumeBetoneira;
  const consideradoAgregadoBetoneira = (peso) => arredondarCincoGramas(consideradoBetoneira(peso));
  const consideradoAguaBetoneira = () =>
    volumeBetoneira === null ? null : Math.round(volumeAgua) * volumeBetoneira;
  const finalBetoneira = (peso, sobra) => {
    const considerado = consideradoBetoneira(peso);
    return considerado === null ? null : considerado + (numero(sobra) || 0);
  };
  const finalAguaBetoneira = (sobra) => {
    const considerado = consideradoAguaBetoneira();
    return considerado === null ? null : considerado + (numero(sobra) || 0);
  };
  const finalAgregadoBetoneira = (peso) => consideradoAgregadoBetoneira(peso);
  const volumeMlBetoneira = (peso, sobra, densidade) => {
    const massaEspecifica = numero(densidade);
    const final = finalBetoneira(peso, sobra);
    return massaEspecifica && final !== null ? final / massaEspecifica : null;
  };
  const idadesRuptura = [0, 1, 2, 3, 4].map((index) => {
    const padrao = ["3", "7", "", "28", ""][index];
    const valor = dosagem[`idadeRuptura${index}`] ?? padrao;
    return {
      index,
      label: valor ? `${valor} dias` : "-",
      valor,
    };
  });
  const idadesRendimento = idadesRuptura;
  const valorRupturaCp = (cp, idade) =>
    dosagem[`rupturaCp${cp}Idade${idade.index}`] ?? dosagem[`rupturaCp${cp}${idade.label}`] ?? "";
  const maximaRuptura = (idade) => {
  if (!idade.valor) return null;

  const cp1 = valorRupturaTon(dosagem, 1, idade)
    ? numero(valorRupturaCp(1, idade))
    : null;

  const cp2 = valorRupturaTon(dosagem, 2, idade)
    ? numero(valorRupturaCp(2, idade))
    : null;

  const maxima = Math.max(cp1 ?? 0, cp2 ?? 0);
  return maxima || null;
};
  const rendimento = (idade) => {
    const valor = maximaRuptura(idade);
    return pesoLigante ? valor / pesoLigante : null;
  };
  const crescimento = (idade) => {
    const valor = rendimento(idade);
    const indiceAtual = idadesRendimento.findIndex((item) => item.index === idade.index);
    const idadeAnterior = [...idadesRendimento]
      .slice(0, indiceAtual)
      .reverse()
      .find((item) => item.valor);
    const anterior = idadeAnterior ? rendimento(idadeAnterior) : null;
    return valor !== null && valor && anterior !== null ? ((valor - anterior) / valor) * 100 : null;
  };
  const rupturas = idadesRuptura
    .filter((idade) => idade.valor)
    .map((idade) => ({
      idade: idade.label.toUpperCase(),
      valor: maximaRuptura(idade) || 0,
    }));
  const rupturaMaxima = Math.max(1, ...rupturas.map((item) => item.valor));
  const teorArEncontradoInformado = numero(dosagem.teorArEncontrado);
  const teorArEncontrado = teorArEncontradoInformado ?? teorArConsiderado;
  const volumeArEncontrado = teorArEncontrado * 10;
  const ajusteBetoneiraPorMetroCubico = (valor) =>
    volumeBetoneira && volumeBetoneira > 0
      ? (numero(valor) || 0) / volumeBetoneira
      : 0;
  const pesoAdicaoCorrigido = (index) =>
    pesoAdicao(index) + ajusteBetoneiraPorMetroCubico(dosagem[`adicaoSobra${index}`]);
  const volumeAdicaoCorrigido = (index) =>
    materialVazio(adicoesVisiveis[index])
      ? 0
      : pesoAdicaoCorrigido(index) / (numero(adicoesVisiveis[index]?.massaEspecifica) || 1);
  const volumeAdicoesCorrigidas = adicoesVisiveis.reduce(
    (total, _, index) => total + volumeAdicaoCorrigido(index),
    0
  );
  const pesoLiganteCorrigido =
    pesoCimento +
    adicoesVisiveis.reduce((total, _, index) => total + pesoAdicaoCorrigido(index), 0);
  const pesoAguaCorrigido =
    pesoAgua + ajusteBetoneiraPorMetroCubico(dosagem.aguaSobra);
  const volumeAguaCorrigido = pesoAguaCorrigido / densidadeAgua;
  const pesoAditivoCorrigido = (index) =>
    pesoAditivo(index) +
    ajusteBetoneiraPorMetroCubico(dosagem[`aditivoSobra${index}`]);
  const percentualAditivoCorrigido = (index) =>
    pesoLiganteCorrigido ? (pesoAditivoCorrigido(index) / pesoLiganteCorrigido) * 100 : 0;
  const volumeAditivoCorrigido = (index) =>
    pesoAditivoCorrigido(index) / (numero(materiaisClassificados.aditivos[index]?.massaEspecifica) || 1);
  const volumeAditivosCorrigidos = materiaisClassificados.aditivos.reduce(
    (total, _, index) => total + volumeAditivoCorrigido(index),
    0
  );
  const volumeNaoAgregadosCorrigido =
    volumeCimento + volumeAdicoesCorrigidas + volumeAguaCorrigido + volumeAditivosCorrigidos + volumeArEncontrado;
  const volumeAgregadosCorrigido = Math.max(0, 1000 - volumeNaoAgregadosCorrigido);
  const massaAgregadosCorrigida =
    divisorVolumeAgregados > 0
      ? volumeAgregadosCorrigido / divisorVolumeAgregados
      : 0;
  const pesoAgregadoCorrigido = (index) => {
    const participacao =
      somaParticipacaoAgregados > 0
        ? percentualAgregadoTotal(index) / somaParticipacaoAgregados
        : 0;
    return massaAgregadosCorrigida * participacao;
  };
  const volumeAgregadoCorrigido = (material, index) =>
    pesoAgregadoCorrigido(index) / (numero(material.densidade) || 1);
  const pesoAgregadosMiudosCorrigidos = materiaisClassificados.agregados.reduce(
    (total, material, index) =>
      total + (material.tipoAgregado === "miudo" ? pesoAgregadoCorrigido(index) : 0),
    0
  );
  const pesoAgregadosGraudosCorrigidos = materiaisClassificados.agregados.reduce(
    (total, material, index) =>
      total + (material.tipoAgregado === "graudo" ? pesoAgregadoCorrigido(index) : 0),
    0
  );
  const teorArgamassaCorrigido =
    pesoLiganteCorrigido + pesoAgregadosMiudosCorrigidos + pesoAgregadosGraudosCorrigidos
      ? ((pesoLiganteCorrigido + pesoAgregadosMiudosCorrigidos) /
          (pesoLiganteCorrigido + pesoAgregadosMiudosCorrigidos + pesoAgregadosGraudosCorrigidos)) * 100
      : null;
  const relacaoAcCorrigida = pesoLiganteCorrigido ? pesoAguaCorrigido / pesoLiganteCorrigido : null;
  const somaPesosCorrigidos =
    pesoCimento +
    adicoesVisiveis.reduce((total, _, index) => total + pesoAdicaoCorrigido(index), 0) +
    pesoAguaCorrigido +
    materiaisClassificados.aditivos.reduce((total, _, index) => total + pesoAditivoCorrigido(index), 0) +
    materiaisClassificados.agregados.reduce((total, _, index) => total + pesoAgregadoCorrigido(index), 0);
  const fatorCorrecaoCalculado = somaPesos ? somaPesosCorrigidos / somaPesos : null;
  const linhasCorrigidas = [
    {
      material: "CIMENTO",
      procedencia: materiaisClassificados.cimento.descricao || materiaisClassificados.cimento.fornecedor || "-",
      peso: pesoCimento,
      volume: volumeCimento,
    },
    ...adicoesVisiveis.filter((adicao) => !materialVazio(adicao)).map((adicao, index) => ({
      material: adicao.descricao || `ADIÇÃO ${index + 1}`,
      procedencia: adicao.fornecedor || "-",
      peso: pesoAdicaoCorrigido(index),
      volume: volumeAdicaoCorrigido(index),
    })),
    ...materiaisClassificados.agregados.map((material, index) => ({
      material: material.material,
      tipo: "agregado",
      procedencia: material.procedencia || material.localColeta || "-",
      peso: pesoAgregadoCorrigido(index),
      volume: volumeAgregadoCorrigido(material, index),
    })),
    {
      material: "Água",
      tipo: "agua",
      procedencia: materiaisClassificados.agua.descricao || "-",
      peso: pesoAguaCorrigido,
      volume: pesoAguaCorrigido,
    },
  ];
  const pesoSacoCimento = numero(dosagem.pesoSacoCimento) ?? 50;
  const fatorSacoCimento = pesoCimento > 0 ? pesoSacoCimento / pesoCimento : 0;
  const pesoAgregadoPorSaco = (index) => pesoAgregadoCorrigido(index) * fatorSacoCimento;
  const medidaPadiolaA = (index) => numero(dosagem[`medidaPadiolaA${index}`]) ?? 35;
  const medidaPadiolaB = (index) => numero(dosagem[`medidaPadiolaB${index}`]) ?? 45;
  const areaPadiolaMaterial = (index) => (medidaPadiolaA(index) * medidaPadiolaB(index)) / 1000;
  const volumePadiolaMaterial = (material, index) => {
    const massaUnitaria = numero(material.massaUnitaria);
    return massaUnitaria && fatorSacoCimento > 0
      ? pesoAgregadoPorSaco(index) / massaUnitaria
      : null;
  };
  const quantidadePadiolas = (index) => numero(dosagem[`quantidadePadiolas${index}`]) ?? 2;
  const alturaPadiola = (material, index) => {
    const quantidade = quantidadePadiolas(index);
    const volume = volumePadiolaMaterial(material, index);
    const areaPadiola = areaPadiolaMaterial(index);
    return quantidade && areaPadiola ? volume / quantidade / areaPadiola : null;
  };

  function imprimirTabelaCalculoTraco() {
    const tabelaOriginal = document.querySelector(".dosagem-tabela-calculo-impressao");
    if (!tabelaOriginal) return;

    const tabelaClonada = tabelaOriginal.cloneNode(true);
    const camposOriginais = tabelaOriginal.querySelectorAll("input, textarea, select");
    const camposClonados = tabelaClonada.querySelectorAll("input, textarea, select");

    camposClonados.forEach((campo, index) => {
      const original = camposOriginais[index];
      if (!original) return;
      campo.value = original.value;
      campo.setAttribute("value", original.value);
      if (campo.tagName === "TEXTAREA") campo.textContent = original.value;
      if (original.type === "checkbox" || original.type === "radio") {
        campo.checked = original.checked;
        if (original.checked) campo.setAttribute("checked", "checked");
        else campo.removeAttribute("checked");
      }
    });

    tabelaClonada
      .querySelector(".informacoes-percentuais-agregados-impressao")
      ?.remove();
    tabelaClonada
      .querySelector(".linha-massa-especifica-ponderada-impressao")
      ?.remove();

    setHtmlInformacoesTraco(tabelaClonada.outerHTML);
    setModoPrintInformacoesTraco(true);
  }

  return (
    <>
      <Navbar />

      <Box className="dosagem-page">
        <Box className="dosagem-actions">
          <Box className="dosagem-actions-titulos">
            <Typography component="h1">Estudo de Dosagem Experimental de Concreto</Typography>
            <Typography component="p">
              {formatarCodigoDosagem(dosagem.codigo)}&nbsp;&nbsp; Rev.
              {obterNumeroRevisao(dosagem)} - {formatarDataBR(dosagem.data)}
            </Typography>
          </Box>
          <Box className="dosagem-actions-botoes">
            {temAlteracoes && !somenteLeitura ? (
              <>
                <Button variant="contained" onClick={salvarDosagem} disabled={salvando}>
                  {salvando ? "Salvando..." : "Salvar"}
                </Button>
                <Button data-confirmacao-cancelamento="propria" variant="outlined" onClick={solicitarCancelarAlteracoes} disabled={salvando}>
                  Cancelar alterações
                </Button>
              </>
            ) : (
              <>
                <Tooltip title="Visualizar relatório">
                  <IconButton color="primary" onClick={() => setModoPrint(true)} aria-label="Imprimir relatório">
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Imprimir informações do traço experimental">
                  <IconButton
                    color="primary"
                    onClick={imprimirTabelaCalculoTraco}
                    aria-label="Imprimir informações consideradas para cálculo do traço experimental"
                  >
                    <PrintIcon />
                  </IconButton>
                </Tooltip>
                {!somenteLeitura && (
                  <Tooltip title="Relatório fotográfico">
                    <IconButton color="primary" onClick={abrirCadastroFotos} aria-label="Relatório fotográfico">
                      <PhotoLibraryIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Imprimir guia de dosagem">
                  <IconButton color="primary" onClick={() => setModoPrintGuia(true)} aria-label="Imprimir guia de dosagem">
                    <AssignmentIcon />
                  </IconButton>
                </Tooltip>
                <Button variant="outlined" onClick={() => history.goBack()} disabled={salvando} startIcon={<ArrowBackIcon />}>Voltar</Button>
              </>
            )}
          </Box>
        </Box>

        <Dialog
          open={openFotos}
          onClose={(event, reason) => {
            if (uploadingFotoIndex !== null || reason === "backdropClick") return;
            setOpenFotos(false);
          }}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Relatório fotográfico</DialogTitle>
          <DialogContent dividers>
            <Typography className="fotos-dialog-subtitulo">
              As primeiras imagens são preenchidas automaticamente com a primeira foto cadastrada de cada agregado e podem ser substituídas apenas neste relatório. Depois delas, cadastre as fotos dos {quantidadeCpsDosagem(dosagem)} CP(s) e outras imagens necessárias.
            </Typography>

            <Box className="fotos-dosagem-grid">
              {fotosDraft.map((foto, index) => (
                <Paper className="foto-dosagem-card" key={foto.id || index} variant="outlined">
                  <Box className="foto-dosagem-preview">
                    {foto.erroImagem ? (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
                        Imagem indisponível. Use o botão abaixo para selecionar outra foto.
                      </Typography>
                    ) : foto.previewUrl || foto.url ? (
                      <img src={foto.previewUrl || foto.url} alt={foto.legenda || `Foto ${index + 1}`} onError={() => setFotosDraft(prev => prev.map((item, itemIndex) => itemIndex === index && item.url === foto.url && item.previewUrl === foto.previewUrl ? { ...item, erroImagem: true } : item))} />
                    ) : (
                      <AddPhotoAlternateIcon />
                    )}
                  </Box>

                  <TextField
                    size="small"
                    label="Legenda"
                    value={foto.legenda}
                    onChange={(e) => atualizarLegendaFoto(index, e.target.value)}
                    fullWidth
                  />

                  <Stack direction="row" spacing={1} justifyContent="space-between">
                    <Button
                      component="label"
                      size="small"
                      variant="contained"
                      disabled={somenteLeitura || uploadingFotoIndex !== null}
                    >
                      {foto.arquivo
                        ? "Selecionada"
                        : foto.origem === ORIGEM_FOTO_AGREGADO
                        ? "Substituir imagem"
                        : "Imagem"}
                      <input
                        hidden
                        accept="image/*"
                        type="file"
                        onChange={(e) => {
                          selecionarImagemFoto(index, e.target.files?.[0]);
                          e.target.value = "";
                        }}
                      />
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removerImagemFoto(index)}
                      disabled={
                        foto.origem === ORIGEM_FOTO_AGREGADO ||
                        (!foto.url && !foto.previewUrl) ||
                        uploadingFotoIndex !== null
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Paper>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={adicionarFotoExtra} startIcon={<AddPhotoAlternateIcon />} disabled={uploadingFotoIndex !== null}>
              Acrescentar imagem
            </Button>
            <Button onClick={cancelarFotos} disabled={uploadingFotoIndex !== null}>
              Cancelar
            </Button>
            <Button onClick={salvarFotos} variant="contained" disabled={uploadingFotoIndex !== null}>
              {uploadingFotoIndex !== null ? "Salvando..." : "Salvar"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={recorteFoto.open}
          onClose={fecharRecorteFoto}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Recortar foto do relatório</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Arraste a foto para enquadrar a parte desejada. Esta área tem a mesma proporção do relatório.
            </Typography>

            <Box
              ref={areaRecorteFotoRef}
              onPointerDown={iniciarArrasteFoto}
              onPointerMove={moverFotoRecorte}
              onPointerUp={finalizarArrasteFoto}
              onPointerCancel={finalizarArrasteFoto}
              sx={{
                position: "relative",
                width: "100%",
                aspectRatio: `${larguraRecorteFotoDosagem} / ${alturaRecorteFotoDosagem}`,
                overflow: "hidden",
                backgroundColor: "#111",
                border: "2px solid",
                borderColor: "primary.main",
                borderRadius: 1,
                cursor: "grab",
                touchAction: "none",
                "&:active": { cursor: "grabbing" },
              }}
            >
              {recorteFoto.url && (
                <Box
                  component="img"
                  src={recorteFoto.url}
                  alt="Pré-visualização do recorte"
                  onLoad={(event) => {
                    const larguraImagem = event.currentTarget.naturalWidth;
                    const alturaImagem = event.currentTarget.naturalHeight;
                    setRecorteFoto((prev) => ({
                      ...prev,
                      larguraImagem,
                      alturaImagem,
                    }));
                  }}
                  sx={estiloImagemRecorteFoto()}
                />
              )}
            </Box>

            <Typography fontWeight={700} mt={3}>Zoom</Typography>
            <Slider
              value={recorteFoto.zoom}
              min={1}
              max={3}
              step={0.05}
              onChange={(event, valor) =>
                setRecorteFoto((prev) => ({ ...prev, zoom: valor }))
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={fecharRecorteFoto}>Cancelar</Button>
            <Button variant="contained" onClick={confirmarRecorteFoto}>
              Aplicar recorte
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openConfirmarCancelamento}
          onClose={() => setOpenConfirmarCancelamento(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Cancelar alterações</DialogTitle>
          <DialogContent dividers>
            <Typography>
              Ao confirmar você perderá todas as alterações. Deseja continuar?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenConfirmarCancelamento(false)}>
              Não
            </Button>
            <Button color="error" variant="contained" onClick={confirmarCancelarAlteracoes}>
              Sim, cancelar
            </Button>
          </DialogActions>
        </Dialog>

        <Paper
          className="dosagem-documento"
          elevation={0}
          onChangeCapture={somenteLeitura ? (event) => {
            event.preventDefault();
            event.stopPropagation();
          } : undefined}
          onKeyDownCapture={somenteLeitura ? (event) => {
            if (["Tab", "PageUp", "PageDown", "Home", "End", "ArrowUp", "ArrowDown"].includes(event.key)) return;
            event.preventDefault();
            event.stopPropagation();
          } : undefined}
          sx={somenteLeitura ? {
            "& input, & textarea, & .MuiSelect-select, & .MuiCheckbox-root, & .MuiRadio-root": {
              pointerEvents: "none",
            },
            "& input, & textarea": {
              userSelect: "text",
            },
            "& .MuiInput-underline:before, & .MuiInput-underline:after": {
              borderBottomColor: "transparent",
            },
          } : undefined}
        >
          <section className="dosagem-bloco dosagem-bloco-info dosagem-bloco-interessado">
            <table className="dosagem-info">
              <tbody>
                <tr>
                  <td><strong>INTERESSADO:</strong></td>
                  <td>{dosagem.clienteNome || ""}</td>
                </tr>
                <tr>
                  <td><strong>OBRA:</strong></td>
                  <td>{dosagem.obraNome || ""}</td>
                </tr>
              </tbody>
            </table>
          </section>

{db?.app?.options?.projectId === "qualiinfra-sgq" && (
<section className="dosagem-bloco dosagem-pre-dimensionamento">
  <div className="dosagem-bloco-topo">
    {preDimensionamentoVisivel && (
      <div className="dosagem-bloco-titulo">Pré-dimensionamento automático</div>
    )}
    <Button
      size="small"
      variant="outlined"
      startIcon={preDimensionamentoVisivel ? <VisibilityOffIcon /> : <VisibilityIcon />}
      onClick={() => setPreDimensionamentoVisivel((visivel) => !visivel)}
    >
      {preDimensionamentoVisivel ? "Ocultar pré-dimensionamento" : "Pré-dimensionamento"}
    </Button>
  </div>

  {preDimensionamentoVisivel && (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(165px, 1fr))",
          justifyContent: "start",
          columnGap: 1,
          rowGap: 1.5,
          mb: 1,
        }}
      >
        <TextField
          size="small"
          label="Fck considerado (MPa)"
          value={numeroInicial(dosagem.fck) ?? ""}
          InputProps={{ readOnly: true }}
          helperText={`Valor original: ${dosagem.fck || "-"}`}
        />

        <TextField
          size="small"
          label="Sd informado (MPa)"
          value={dosagem.sd || ""}
          onChange={(e) => atualizarCampo("sd", e.target.value)}
        />

        <TextField
          size="small"
          label="Fcj calculado (MPa)"
          value={fcjCalculado !== null ? formatarNumeroCampo(fcjCalculado, 1) : ""}
          InputProps={{ readOnly: true }}
        />

        <TextField
          size="small"
          label="Fator a/c sugerido"
          value={fatorAcSugerido !== null ? formatarNumeroCampo(fatorAcSugerido, 2) : ""}
          InputProps={{ readOnly: true }}
        />

        <TextField
          size="small"
          label="Slump desejado (mm)"
          value={dosagem.slump || ""}
          onChange={(e) => atualizarCampo("slump", e.target.value)}
        />

        <TextField
          size="small"
          label="Tolerância do Slump (mm)"
          value={dosagem.toleranciaSlumpProjeto || ""}
          onChange={(e) => atualizarCampo("toleranciaSlumpProjeto", e.target.value)}
        />

        <TextField
          size="small"
          label="DMC agregado graúdo (mm)"
          value={diametroMaximoGraudo !== null ? formatarNumeroCampo(diametroMaximoGraudo, 1) : ""}
          InputProps={{ readOnly: true }}
          helperText="Buscado da caracterização"
        />

        <TextField
          size="small"
          label="Módulo de finura da areia"
          value={moduloFinuraAreia !== null ? formatarNumeroCampo(moduloFinuraAreia, 2) : ""}
          InputProps={{ readOnly: true }}
          helperText="Buscado da caracterização"
        />

        <TextField
          size="small"
          label="Água estimada (kg/m³)"
          value={aguaEstimada !== null ? formatarNumeroCampo(aguaEstimada, 1) : ""}
          InputProps={{ readOnly: true }}
        />

        <TextField
          size="small"
          label="Cimento calculado (kg/m³)"
          value={cimentoCalculado !== null ? formatarNumeroCampo(cimentoCalculado, 0) : ""}
          InputProps={{ readOnly: true }}
        />

        <TextField
          size="small"
          label="Teor argamassa sugerido (%)"
          value={formatarNumeroCampo(teorArgamassaSugerido, 1)}
          InputProps={{ readOnly: true }}
        />

        <TextField
          size="small"
          label="Areia sugerida (%)"
          value={percentualAreiaSugerido !== null ? formatarNumeroCampo(percentualAreiaSugerido, 1) : ""}
          InputProps={{ readOnly: true }}
        />

        <TextField
          size="small"
          label="Brita sugerida (%)"
          value={percentualBritaSugerido !== null ? formatarNumeroCampo(percentualBritaSugerido, 1) : ""}
          InputProps={{ readOnly: true }}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        {!somenteLeitura && (
          <Button
            variant="contained"
            onClick={aplicarPreDimensionamento}
            disabled={
              cimentoCalculado === null ||
              percentualAreiaSugerido === null ||
              percentualBritaSugerido === null
            }
          >
            Aplicar pré-dimensionamento na tabela
          </Button>
        )}
      </Box>
    </>
  )}
</section>
)}

          <section className="dosagem-bloco">
            <div className="dosagem-bloco-titulo">Dados do Traço</div>
            <Table className="dosagem-tabela" size="small">
              <TableHead>
                <TableRow>
                  <TableCell>TRACO</TableCell>
                  <TableCell>Nº REVISÃO</TableCell>
                  <TableCell>Fator a/c</TableCell>
                  <TableCell>Fck (MPa/dias)</TableCell>
                  <TableCell>Sd (MPa)</TableCell>
                  <TableCell>Sd / fckest (MPa)</TableCell>
                  <TableCell>Slump (mm)</TableCell>
                  <TableCell>Tolerância Slump (mm)</TableCell>
                  <TableCell>TIPO DE CONCRETO</TableCell>
                  <TableCell>CP'S</TableCell>
                  <TableCell>DATA</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow className="linha-verde">
                  <TableCell><TextField variant="standard" value={dosagem.traco || ""} onChange={(e) => atualizarCampo("traco", e.target.value)} /></TableCell>
                  <TableCell>
                    <TextField
                      variant="standard"
                      value={dosagem.numeroRevisao ?? "0"}
                      onChange={(e) =>
                        atualizarCampo(
                          "numeroRevisao",
                          e.target.value.replace(/\D/g, "")
                        )
                      }
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    />
                  </TableCell>
                  <TableCell><TextField variant="standard" value={dosagem.fatorAc || ""} onChange={(e) => atualizarCampo("fatorAc", e.target.value)} /></TableCell>
                  <TableCell><TextField variant="standard" value={dosagem.fck || ""} onChange={(e) => atualizarCampo("fck", e.target.value)} /></TableCell>
                  <TableCell><TextField variant="standard" value={dosagem.sd || ""} onChange={(e) => atualizarCampo("sd", e.target.value)} /></TableCell>
                  <TableCell>
  <TextField
    variant="standard"
    value={dosagem.fckest || ""}
    InputProps={{ readOnly: true }}
  />
</TableCell>
                  <TableCell><TextField variant="standard" value={dosagem.slump || ""} onChange={(e) => atualizarCampo("slump", e.target.value)} /></TableCell>
                  <TableCell><TextField variant="standard" value={dosagem.toleranciaSlumpProjeto || ""} onChange={(e) => atualizarCampo("toleranciaSlumpProjeto", e.target.value)} /></TableCell>
                  <TableCell><TextField variant="standard" value={dosagem.tipoConcreto || ""} onChange={(e) => atualizarCampo("tipoConcreto", e.target.value)} /></TableCell>
                  <TableCell><TextField variant="standard" value={dosagem.cps || ""} onChange={(e) => atualizarCampo("cps", e.target.value)} /></TableCell>
                  <TableCell>{formatarDataBR(dosagem.data)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Box sx={{ mt: 2, maxWidth: 360 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Tipo de guia de dosagem"
                value={dosagem.tipoGuiaDosagem || "acumulada"}
                onChange={(e) => atualizarCampo("tipoGuiaDosagem", e.target.value)}
              >
                <MenuItem value="acumulada">Acumulada</MenuItem>
                <MenuItem value="individual">Pesos individuais por silo</MenuItem>
              </TextField>
            </Box>
            <Box sx={{ mt: 2 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Prensa utilizada para ruptura"
                value={dosagem.prensaRuptura?.id || ""}
                onChange={(e) => {
                  const prensa = calibracoesPrensa.find((item) => item.id === e.target.value);

                  if (prensa) {
                    const datasRuptura = ["3", "7", "", "28", ""].map((padrao, index) => {
                      const idade = dosagem[`idadeRuptura${index}`] ?? padrao;
                      return idade ? adicionarDiasDataBR(dosagem.data, idade) : "";
                    });

                    if (!confirmarPrensaCalibradaParaRupturas(prensa, datasRuptura)) return;
                  }

                  atualizarCampo("prensaRuptura", prensa ? { id: prensa.id, ...prensa } : null);
                }}
              >
                <MenuItem value="">Selecione a prensa/certificado</MenuItem>
                {calibracoesPrensa.map((prensa) => (
                  <MenuItem key={prensa.id} value={prensa.id}>
                    {formatarPrensaRuptura(prensa)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </section>

          <section className="dosagem-bloco dosagem-tabela-calculo-impressao">
            <div className="dosagem-bloco-titulo">Informações consideradas para cálculo do traço experimental</div>
            {!!materiaisClassificados.agregados.length && (
              <Box
                className="informacoes-percentuais-agregados-impressao"
                sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, px: 1, py: 1 }}
              >
                {indiceAgregadoMiudo >= 0 && (
                  <TextField
                    size="small"
                    label="Agregado miúdo (% do total de agregados)"
                    value={dosagem[`materialParcial${indiceAgregadoMiudo}`] || ""}
                    onChange={(e) => atualizarCampo(`materialParcial${indiceAgregadoMiudo}`, e.target.value)}
                    inputProps={{ inputMode: "decimal", style: { textAlign: "center" } }}
                    sx={{ width: 260 }}
                  />
                )}
                {!!indicesAgregadosGraudos.length && (
                  <TextField
                    size="small"
                    label="Agregado graúdo (% do total de agregados)"
                    value={numeroBR(percentualGraudo, 1)}
                    InputProps={{ readOnly: true }}
                    inputProps={{ style: { textAlign: "center" } }}
                    sx={{ width: 270 }}
                  />
                )}
              </Box>
            )}
            <table className="dosagem-composicao dosagem-composicao-materiais">
            <colgroup>
              <col className="col-material-codigo" />
              <col className="col-material-tipo" />
              <col className="col-material-descricao" />
              <col className="col-material-fornecedor" />
              <col className="col-proporcao" />
              <col className="col-parcial" />
              <col className="col-total-percentual" />
              <col className="col-peso" />
              <col className="col-massa-esp" />
              <col className="col-volume" />
              <col className="col-considerado" />
              <col className="col-sobra" />
              <col className="col-final" />
            </colgroup>
            <thead>
              <tr>
  <th className="titulo-codigo-material" rowSpan="3">CÓD.</th>
  <th className="titulo-materiais-constituintes" colSpan="3" rowSpan="3">
    Materiais Constituintes
  </th>
  <th rowSpan="3">Proporção</th>
  <th rowSpan="3">% parcial</th>
  <th rowSpan="3">% total</th>
  <th rowSpan="3">Peso (kg)</th>
  <th rowSpan="3">M. Esp. (kg/dm3)</th>
  <th rowSpan="3">Vol. (L)</th>
  <th className="betoneira-titulo" colSpan="3">TRAÇO PARA BETONEIRA</th>
</tr>
              <tr>
                <th className="betoneira-volume" colSpan="3">
                  Volume para:
                  <TextField
                    variant="standard"
                    value={dosagem.volumeBetoneira || ""}
                    onChange={(e) => atualizarCampo("volumeBetoneira", e.target.value)}
                  />
                  Litros - massa dos materiais em (g)
                </th>
              </tr>
              <tr>
                <th>Considerado (g)</th>
                <th>Adic. ou sobra</th>
                <th>(g)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{codigoOuTraco(materiaisClassificados.cimento)}</td>
                <td>Cimento</td>
                <td>{valorOuTraco(materiaisClassificados.cimento.descricao)}</td>
                <td>{valorOuTraco(materiaisClassificados.cimento.fornecedor)}</td>
                <td>-</td>
                <td>-</td>
                <td>{numeroBR(percentualTotal(pesoCimento), 1)}</td>
                <td><TextField variant="standard" value={dosagem.cimentoPeso || ""} onChange={(e) => atualizarCampo("cimentoPeso", e.target.value)} inputProps={{ "data-dosagem-campo": "cimentoPeso" }} onKeyDown={(e) => irParaCampoDosagem(e, materiaisClassificados.aditivos.length ? "aditivoParcial0" : `materialProporcaoGrupo${[...indicesAgregadosMiudos, ...indicesAgregadosGraudos][0]}`)} /></td>
                <td><TextField variant="standard" value={dosagem.cimentoDensidade || materiaisClassificados.cimento.massaEspecifica || "3,100"} InputProps={{ readOnly: true }} inputProps={{ "data-dosagem-campo": "cimentoDensidade" }} onKeyDown={(e) => irParaCampoDosagem(e, materiaisClassificados.aditivos.length ? "aditivoParcial0" : `materialProporcaoGrupo${[...indicesAgregadosMiudos, ...indicesAgregadosGraudos][0]}`)} /></td>
                <td>{numeroBR(volumeCimento, 1)}</td>
                <td>{numeroBRBetoneira(consideradoBetoneira(pesoCimento))}</td>
                <td>-</td>
                <td className="amarelo">{numeroBRBetoneira(finalBetoneira(pesoCimento, 0))}</td>
              </tr>
              {adicoesVisiveis.map((adicao, index) => {
                const semMaterial = materialVazio(adicao);

                return (
                  <tr key={adicao.id || index}>
                    <td>{codigoOuTraco(adicao)}</td>
                    <td>{index === 0 ? "ADIÇÃO" : `ADIÇÃO ${index + 1}`}</td>
                    <td>{valorOuTraco(adicao.descricao)}</td>
                    <td>{valorOuTraco(adicao.fornecedor)}</td>
                    <td>-</td>
                    <td>
                      {semMaterial
                        ? "-"
                        : <TextField variant="standard" value={dosagem[`adicaoParcial${index}`] || ""} onChange={(e) => atualizarCampo(`adicaoParcial${index}`, e.target.value)} inputProps={{ "data-dosagem-coluna": "parcial" }} onKeyDown={navegarEntreParcialEProporcao} />}
                    </td>
                    <td>{semMaterial ? "-" : numeroBR(percentualTotal(pesoAdicao(index)), 1)}</td>
                    <td>{semMaterial ? "-" : numeroBR(pesoAdicao(index), 1)}</td>
                    <td>{valorOuTraco(adicao.massaEspecifica)}</td>
                    <td>{semMaterial ? "-" : numeroBR(volumeAdicao(index), 1)}</td>
                    <td>{semMaterial ? "-" : numeroBRBetoneira(consideradoBetoneira(pesoAdicao(index)))}</td>
                    <td>
                      {semMaterial
                        ? "-"
                        : <TextField variant="standard" value={dosagem[`adicaoSobra${index}`] || ""} onChange={(e) => atualizarCampo(`adicaoSobra${index}`, e.target.value)} />}
                    </td>
                    <td className="amarelo">
                      {semMaterial ? "-" : numeroBRBetoneira(finalBetoneira(pesoAdicao(index), dosagem[`adicaoSobra${index}`]))}
                    </td>
                  </tr>
                );
              })}
             <tr>
              <td>{codigoOuTraco(materiaisClassificados.agua)}</td>
              <td>Água</td>
              <td>
                {dosagem.aguaId || dosagem.aguaResumo?.id ? (
                    valorOuTraco(materiaisClassificados.agua.descricao)
                  ) : (
                    <TextField
                      variant="standard"
                      value={dosagem.aguaDescricao || ""}
                      onChange={(e) => atualizarCampo("aguaDescricao", e.target.value)}
                      placeholder="Origem ou descrição da água"
                    />
                  )}
                </td>
                <td>
                  {dosagem.aguaId || dosagem.aguaResumo?.id ? (
                    valorOuTraco(materiaisClassificados.agua.fornecedor)
                  ) : (
                    <TextField
                      variant="standard"
                      value={dosagem.aguaFornecedor || ""}
                      onChange={(e) => atualizarCampo("aguaFornecedor", e.target.value)}
                      placeholder="Fornecedor ou local"
                    />
                  )}
                </td>
                <td>-</td>
                <td>-</td>
                <td>{numeroBR(percentualTotal(pesoAgua), 1)}</td>
                <td>{numeroBR(pesoAgua, 0)}</td>
                <td>{valorOuTraco(materiaisClassificados.agua.massaEspecifica || "1,000")}</td>
                <td>{numeroBR(volumeAgua, 0)}</td>
                <td>{numeroBRBetoneira(consideradoAguaBetoneira())}</td>
                <td><TextField variant="standard" value={dosagem.aguaSobra || ""} onChange={(e) => atualizarCampo("aguaSobra", e.target.value)} /></td>
                <td className="amarelo">{numeroBRBetoneira(finalAguaBetoneira(dosagem.aguaSobra))}</td>
              </tr>
              {materiaisClassificados.aditivos.map((aditivo, index) => (
  <tr key={aditivo.id || index}>
    <td>{codigoOuTraco(aditivo)}</td>
    <td>{`Aditivo ${index + 1}`}</td>
    <td>{valorOuTraco(aditivo.descricao)}</td>
    <td>{valorOuTraco(aditivo.fornecedor)}</td>
    <td>-</td>
                  <td><TextField variant="standard" value={dosagem[`aditivoParcial${index}`] || ""} onChange={(e) => atualizarCampo(`aditivoParcial${index}`, e.target.value)} inputProps={{ "data-dosagem-campo": `aditivoParcial${index}`, "data-dosagem-coluna": "parcial" }} onKeyDown={navegarEntreParcialEProporcao} /></td>
                  <td>{numeroBR(percentualTotal(pesoAditivo(index)), 1)}</td>
                  <td>{numeroBR(pesoAditivo(index), 2)}</td>
                  <td>{valorOuTraco(aditivo.massaEspecifica)}</td>
                  <td>{numeroBR(volumeAditivo(index), 2)}</td>
                  <td>{numeroBRBetoneira(consideradoBetoneira(pesoAditivo(index)))}</td>
                  <td><TextField variant="standard" value={dosagem[`aditivoSobra${index}`] || ""} onChange={(e) => atualizarCampo(`aditivoSobra${index}`, e.target.value)} /></td>
                  <td className="celula-aditivo-final">
                    <span className="aditivo-final-gramas">
                      {numeroBRComUnidade(finalBetoneira(pesoAditivo(index), dosagem[`aditivoSobra${index}`]), "g")}
                    </span>
                    <span className="aditivo-final-ml">
                      {numeroBRComUnidade(volumeMlBetoneira(
                        pesoAditivo(index),
                        dosagem[`aditivoSobra${index}`],
                        aditivo.massaEspecifica
                      ), "ml")}
                    </span>
                  </td>
                </tr>
              ))}
              {[...indicesAgregadosMiudos, ...indicesAgregadosGraudos].map((index) => {
                const material = materiaisClassificados.agregados[index];

                return (
                <tr key={material.id || index}>               
                  <td>{codigoOuTraco(material)}</td>
                  <td>{material.material}</td>
                  <td>{material.procedencia}</td>
                  <td>{material.localColeta}</td>
                  <td><TextField variant="standard" value={dosagem[`materialProporcaoGrupo${index}`] || ""} onChange={(e) => atualizarProporcaoAgregado(index, e.target.value)} inputProps={{ "data-dosagem-campo": `materialProporcaoGrupo${index}`, "data-dosagem-coluna": "proporcao" }} onKeyDown={navegarEntreParcialEProporcao} /></td>
                  <td>{numeroBR(percentualParcialAgregado(material, index), 1)}</td>
                  <td>{numeroBR(percentualTotal(pesoAgregado(index)), 1)}</td>
                  <td>{numeroBR(pesoAgregado(index), 0)}</td>
                  <td>{valorOuTraco(material.densidade)}</td>
                  <td>{numeroBR(pesoAgregado(index) / (numero(material.densidade) || 1), 0)}</td>
                  <td>{numeroBRBetoneira(consideradoAgregadoBetoneira(pesoAgregado(index)))}</td>
                  <td>-</td>
                  <td className="amarelo">{numeroBRBetoneira(finalAgregadoBetoneira(pesoAgregado(index)))}</td>
                </tr>
                );
              })}
              <tr>
                <td colSpan="4">Teor de Ar Considerado</td>
                <td><TextField variant="standard" value={dosagem.teorArConsiderado || ""} onChange={(e) => atualizarCampo("teorArConsiderado", e.target.value)} /></td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>{numeroBR(volumeArConsiderado, 1)}</td>
                <td className="rotulo-betoneira" colSpan="2">Massa Específica Obtida (kg/m³)</td>
                <td className="amarelo">
                  <TextField
                    variant="standard"
                    value={dosagem.massaEspecificaObtida || "2,395"}
                    onChange={(e) => atualizarCampo("massaEspecificaObtida", e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <td colSpan="4">Teor de Argamassa considerado</td>
                <td>{numeroBR(teorArgamassaCalculado, 1)}%</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td className="rotulo-betoneira" colSpan="2">Teor de ar encontrado (%)</td>
                <td>
                  <TextField
                    variant="standard"
                    value={dosagem.teorArEncontrado ?? ""}
                    placeholder="-"
                    onChange={(e) => atualizarCampo("teorArEncontrado", e.target.value)}
                  />
                </td>
              </tr>
              <tr className="linha-total">
                <td colSpan="5">TOTAL</td>
                <td>100,0</td>
                <td>{numeroBR(somaPesos, 1)}</td>
                <td>-</td>
                <td>1.000</td>
                <td colSpan="3">F. Correção: {numeroBR(fatorCorrecaoCalculado, 3)}</td>
              </tr>
              <tr className="linha-total linha-massa-especifica-ponderada-impressao">
                <td colSpan="7">Massa específica ponderada dos agregados</td>
                <td>{numeroBR(massaEspecificaPonderadaAgregados, 3)}</td>
                <td colSpan="4">kg/dm3</td>
              </tr>
            </tbody>
          </table>
          </section>

          <section className="dosagem-bloco">
            <table className="dosagem-composicao dosagem-ensaio">
              <colgroup>
                <col className="ensaio-col-label" />
                <col className="ensaio-col-label" />
                <col className="ensaio-col-label" />
                <col className="ensaio-col-idade" />
                <col className="ensaio-col-idade" />
                <col className="ensaio-col-idade" />
                <col className="ensaio-col-idade" />
                <col className="ensaio-col-idade" />
                <col className="ensaio-col-rendimento" />
                <col className="ensaio-col-rendimento" />
                <col className="ensaio-col-rendimento" />
                <col className="ensaio-col-rendimento" />
              </colgroup>
              <tbody>
                <tr>
                  <td colSpan="6" className="ensaio-aspecto">
                    <strong>Aspecto do Concreto:</strong>
                    <div className="opcoes-concreto">
                      {["Excelente", "Bom", "Regular", "Ruim"].map((opcao) => (
                        <label key={opcao}>
                          <input
                            type="radio"
                            name="aspectoConcreto"
                            checked={(dosagem.aspectoConcreto || "Bom") === opcao}
                            onChange={() => atualizarCampo("aspectoConcreto", opcao)}
                          />
                          {opcao}
                        </label>
                      ))}
                    </div>
                    <div className="opcoes-concreto">
                      {["Muita Argamassa", "Argamassa/Ideal", "Pouca Argamassa/Áspero"].map((opcao) => (
                        <label key={opcao}>
                          <input
                            type="radio"
                            name="argamassaAspecto"
                            checked={(dosagem.argamassaAspecto || "Argamassa/Ideal") === opcao}
                            onChange={() => atualizarCampo("argamassaAspecto", opcao)}
                          />
                          {opcao}
                        </label>
                      ))}
                    </div>
                  </td>
                  <td colSpan="3">
                    <strong>Temperaturas (°C)</strong>
                    <div>Ambiente: <TextField variant="standard" value={dosagem.temperaturaAmbiente || "entre 31 e 33°C"} onChange={(e) => atualizarCampo("temperaturaAmbiente", e.target.value)} /></div>
                    <div>Concreto: <TextField variant="standard" value={dosagem.temperaturaConcreto || "entre 29 e 30°C"} onChange={(e) => atualizarCampo("temperaturaConcreto", e.target.value)} /></div>
                  </td>
                  <td colSpan="3">
                    <div className="slump-box">
                      <strong>Abatimento "slump" obtido (cm)</strong>
                      <TextField variant="standard" value={dosagem.abatimentoSlump || ""} onChange={(e) => atualizarCampo("abatimentoSlump", e.target.value)} />
                      <div className="slump-linha">
                        <span>SLUMP INICIAL</span>
                        <TextField variant="standard" value={dosagem.slumpInicial || ""} onChange={(e) => atualizarCampo("slumpInicial", e.target.value)} />
                      </div>
                      <div className="slump-linha">
                        <span>SLUMP FINAL</span>
                        <TextField variant="standard" value={dosagem.slumpFinal || ""} onChange={(e) => atualizarCampo("slumpFinal", e.target.value)} />
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td colSpan="12" className="ruptura-analise-cell">
                    <div className="ruptura-analise-grid">
                      <table className="dosagem-composicao ruptura-tabela">
                        <thead>
                          <tr><th colSpan="6">RUPTURA DOS CORPOS DE PROVA (MPa)</th></tr>
                          <tr>
                            <th>Idade dos corpos de provas</th>
                            {idadesRuptura.map((idade) => (
                              <th key={"idade-" + idade.index}>
                                <CampoNumerico
                                  value={idade.valor}
                                  onChange={(valor) => atualizarCampo("idadeRuptura" + idade.index, valor)}
                                  campo="idadeRuptura"
                                  linha={0}
                                  coluna={idade.index}
                                />
                                {idade.valor ? <span className="sufixo-dias">dias</span> : null}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Data do ensaio</td>
                            {idadesRuptura.map((idade) => (
                              <td key={"data-" + idade.index}>{idade.valor ? adicionarDiasDataBR(dosagem.data, idade.valor) : "-"}</td>
                            ))}
                          </tr>
                          {[1, 2].map((cp, linhaIndex) => (
                            <tr key={"cp-" + cp}>
                              <td>Corpo de Prova nº {cp}</td>
                              {idadesRuptura.map((idade) => (
                                <td key={"cp-" + cp + "-" + idade.index}>
                                  {!idade.valor
                                    ? "-"
                                    : <div className="campo-ruptura-ton-mpa">
  <CampoNumerico
    value={valorRupturaTon(dosagem, cp, idade)}
    onChange={(valor) => {
      atualizarCampo("rupturaCp" + cp + "TonIdade" + idade.index, valor);
      atualizarCampo(
        "rupturaCp" + cp + "Idade" + idade.index,
        toneladaParaMpa(valor, dosagem)
      );
    }}
    campo={"rupturaCp" + cp + "ton"}
    linha={linhaIndex + 1}
    coluna={idade.index}
  />
  <span>ton</span>
  <small>
  {valorRupturaTon(dosagem, cp, idade) ? `${valorRupturaCp(cp, idade) || "-"} MPa` : "-"}
</small>
</div>}
                                </td>
                              ))}
                            </tr>
                          ))}
                          <tr>
                            <td>Máximo</td>
                            {idadesRuptura.map((idade) => (
                              <td key={"max-" + idade.index}>{idade.valor ? numeroBR(maximaRuptura(idade), 1) : "-"}</td>
                            ))}
                          </tr>
                          <tr>
                            <td colSpan={idadesRuptura.length + 1}>
                              Área do CP considerada no cálculo: {numeroBR(areaCpDosagem(dosagem), 3)} cm²
                            </td>
                          </tr>
                        </tbody>
                      </table>

                      <table className="dosagem-composicao analise-tabela">
                        <thead>
                          <tr>
                            <th>ANÁLISE</th>
                            {idadesRendimento.map((idade) => <th key={"rend-" + idade.index}>{idade.label}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Rendimento (MPa/kg/m³)</td>
                            {idadesRendimento.map((idade) => (
                              <td key={"rendvalor-" + idade.index}>{idade.valor ? numeroBR(rendimento(idade), 4) : "-"}</td>
                            ))}
                          </tr>
                          <tr>
                            <td>Crescimento</td>
                            {idadesRendimento.map((idade) => (
                              <td key={"cres-" + idade.index}>{!idade.valor || idade.index === 0 ? "-" : numeroBR(crescimento(idade), 1) + "%"}</td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="dosagem-bloco dosagem-complementar">
            <div className="dosagem-complementar-topo">
              <div className="rupturas-painel">
                <div className="dosagem-bloco-titulo">Rupturas</div>
                <div className="rupturas-chart">
                  <div className="eixo-y-titulo">Resistência (MPa)</div>
                  {[50, 40, 30, 20, 10, 0].map((valor) => (
                    <div
                      className="linha-escala"
                      key={valor}
                      style={{ bottom: `${(valor / 50) * 100}%` }}
                    >
                      <span>{valor}</span>
                    </div>
                  ))}
                  {rupturas.map((ruptura) => (
                    <div className="ruptura-barra-item" key={ruptura.idade}>
                      {ruptura.valor > 0 && (
                        <div className="ruptura-barra" style={{ height: `${(ruptura.valor / rupturaMaxima) * 100}%` }}>
                          <span>{numeroBR(ruptura.valor, 1)}</span>
                        </div>
                      )}
                      <strong>{ruptura.idade}</strong>
                    </div>
                  ))}
                  <div className="eixo-x-titulo">Idade</div>
                </div>
                <Typography className="observacao-grafico">
                  Observação.: {dosagem.observacao || "Para composição do gráfico foi considerado o maior valor de fck obtido no dueto, conforme ABNT NBR 12.655."}
                </Typography>
                <table className="dosagem-composicao calculo-auxiliar">
                  <tbody>
                    <tr><td>Volume Argamassa</td><td>{numeroBR(volumeArgamassaExperimental, 2)}</td></tr>
                    <tr><td>Volume Agregados</td><td>{numeroBR(volumeAgregadosExperimental, 2)}</td></tr>
                    <tr><td>Massa espec. ponderada (g/cm³)</td><td>{numeroBR(massaEspecificaPonderadaAgregados, 3)}</td></tr>
                    <tr><td>Massa dos Agregados</td><td>{numeroBR(massaAgregadosExperimental, 1)}</td></tr>
                    <tr><td>Vol. dos Agregados (L)</td><td>{numeroBR(volumeAgregadosCorrigido, 1)} L</td></tr>
                    <tr><td>Vol. de cimento, água e aditivos (L)</td><td>{numeroBR(volumeNaoAgregadosCorrigido, 1)} L</td></tr>
                    <tr><td>Massa dos Agregados</td><td>{numeroBR(massaAgregadosCorrigida, 1)}</td></tr>
                    <tr><td>Fator A/C</td><td>{valorOuTraco(dosagem.fatorAc)}</td></tr>
                  </tbody>
                </table>
              </div>

              <div className="corrigidos-painel">
                <div className="dosagem-bloco-titulo">DADOS CORRIGIDOS P/ Tabela de Traços (kg/m³)</div>
                <table className="dosagem-composicao dosagem-composicao-materiais">
                  <thead>
                    <tr><th>Material</th><th>Procedência</th><th>Qtd. (kg)</th><th>Vol. (l)</th></tr>
                  </thead>
                  <tbody>
                    {linhasCorrigidas.map((linha, index) => (
                      <tr key={`${linha.material}-${index}`}>
                        <td>{linha.material}</td>
                        <td>{linha.procedencia}</td>
                        <td>{numeroBR(linha.peso, linha.tipo === "agua" || linha.tipo === "agregado" ? 0 : 1)}</td>
                        <td>{numeroBR(linha.volume, linha.tipo === "agua" || linha.tipo === "agregado" ? 0 : 1)}</td>
                      </tr>
                    ))}
                    <tr className="linha-total"><td colSpan={2}>Soma:</td><td>{numeroBR(somaPesosCorrigidos, 1)}</td><td>1.000</td></tr>
                  </tbody>
                </table>
                <div className="dosagem-subtitulo">Aditivo(s)</div>
                <table className="dosagem-composicao dosagem-composicao-materiais">
                  <tbody>
                    {materiaisClassificados.aditivos.map((aditivo, index) => (
                      <tr key={`${aditivo.id || index}-corrigido`}>
                        <td>{`ADITIVO ${index + 1}`}</td>
                        <td>
                          <TextField
                            variant="standard"
                            value={formatarNumeroCampo(percentualAditivoCorrigido(index), 2)}
                            InputProps={{ readOnly: true }}
                          />
                        </td>
                        <td>{numeroBR(pesoAditivoCorrigido(index), 1)}</td>
                        <td>{numeroBR(volumeAditivoCorrigido(index), 1)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td>Teor de Ar</td>
                      <td>
                        <TextField
                          variant="standard"
                          value={formatarNumeroCampo(teorArEncontrado, 1)}
                          placeholder="-"
                          InputProps={{ readOnly: true }}
                        />
                      </td>
                      <td>-</td>
                      <td>{numeroBR(volumeArEncontrado, 1)}</td>
                    </tr>
                  </tbody>
                </table>
                <div className="dosagem-subtitulo">Outras informações</div>
                <table className="dosagem-composicao outras-informacoes-agregados">
                  <tbody>
                    <tr><td colSpan={3}>Relação A/C:</td><td>{numeroBR(relacaoAcCorrigida, 2)}</td></tr>
                    <tr><td colSpan={3}>Teor de Argamassa:</td><td>{numeroBR(teorArgamassaCorrigido, 1)}%</td></tr>
                    {materiaisClassificados.agregados.map((material) => (
                      <tr key={`${material.id}-mu`}>
                        <td>{material.material}</td>
                        <td>{material.procedencia}</td>
                        <td>{material.localColeta}</td>
                        <td>{valorOuTraco(material.massaUnitaria)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="dosagem-volume-controle">
              <Button
                size="small"
                variant="outlined"
                startIcon={dosagemVolumeVisivel ? <VisibilityOffIcon /> : <VisibilityIcon />}
                onClick={async () => {
                  if (!dosagemVolumeVisivel) {
                    const podeAbrir = await atualizarMassasUnitariasPadiolas();
                    if (!podeAbrir) return;
                  }
                  setDosagemVolumeVisivel((visivel) => !visivel);
                }}
              >
                {dosagemVolumeVisivel ? "Ocultar dosagem em volume" : "Visualizar dosagem em volume (padiolas)"}
              </Button>
            </div>

            {dosagemVolumeVisivel && (
              <div className="dosagem-volume-padiolas">
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <strong>Peso do saco de cimento (kg):</strong>
                  <CampoNumerico
                    value={dosagem.pesoSacoCimento ?? "50"}
                    onChange={(valor) => atualizarCampo("pesoSacoCimento", valor)}
                    campo="pesoSacoCimento"
                    linha={0}
                    coluna="peso-saco-cimento"
                    sx={{ width: 90 }}
                  />
                </div>

                <table className="dosagem-composicao medidas-padiolas medidas-padiolas-dinamicas">
                  <tbody>
                    <tr>
                      <th colSpan={5}>medidas padrões das padiolas</th>
                    </tr>
                    {materiaisClassificados.agregados.map((material, index) => (
                      <tr key={`${material.id || index}-medidas`}>
                        <td>{material.material}</td>
                        <td>{material.procedencia}</td>
                        <td>{material.localColeta}</td>
                        <td>
                          <CampoNumerico
                            value={dosagem[`medidaPadiolaA${index}`] ?? "35,0"}
                            onChange={(valor) => atualizarCampo(`medidaPadiolaA${index}`, valor)}
                            campo={`medidaPadiolaA${index}`}
                            linha={index * 2}
                            coluna="medida-padiola"
                          />
                        </td>
                        <td>
                          <CampoNumerico
                            value={dosagem[`medidaPadiolaB${index}`] ?? "45,0"}
                            onChange={(valor) => atualizarCampo(`medidaPadiolaB${index}`, valor)}
                            campo={`medidaPadiolaB${index}`}
                            linha={(index * 2) + 1}
                            coluna="medida-padiola"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="padiolas-grid">
                  {materiaisClassificados.agregados.map((material, index) => (
                    <div className="padiola-card" key={`${material.id}-padiola`}>
                      <div className="padiola-card-titulo">
                        <strong>{material.material}</strong>
                        <span>Volume {numeroBR(volumePadiolaMaterial(material, index), 1)} L</span>
                      </div>
                      <div className="padiola-desenho">
                        <span className="medida medida-largura">{numeroBR(medidaPadiolaB(index), 1)}</span>
                        <span className="medida medida-altura">{numeroBR(alturaPadiola(material, index), 1)}</span>
                        <span className="medida medida-fundo">{numeroBR(medidaPadiolaA(index), 1)}</span>
                      </div>
                      <div className="padiola-rodape">
                        <span>
                          <CampoNumerico
                            value={dosagem[`quantidadePadiolas${index}`] ?? "2"}
                            onChange={(valor) => atualizarCampo(`quantidadePadiolas${index}`, valor)}
                            campo={`quantidadePadiolas${index}`}
                            linha={index}
                            coluna="quantidade-padiolas"
                          />
                          Padiola
                        </span>
                        <span>{numeroBR(medidaPadiolaA(index), 0)} x {numeroBR(medidaPadiolaB(index), 0)} x {numeroBR(alturaPadiola(material, index), 1)}</span>
                        <span>massa: {numeroBR(pesoAgregadoPorSaco(index) / quantidadePadiolas(index), 1)} kg</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

              <div className="observacoes-importantes-editor">
  <div className="observacoes-importantes-topo">
    <strong>METODOLOGIAS - ITEM 2 DO RELATÓRIO:</strong>
    {!somenteLeitura && (
      <Button
        size="small"
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={adicionarMetodologia}
      >
        Nova norma
      </Button>
    )}
  </div>

  {metodologiasRelatorio.map((texto, index) => (
    <div className="observacao-importante-item" key={`metodologia-${index}`}>
      <div className="observacao-importante-cabecalho">
        <span>{`2.${index + 1}`}</span>

        <TextField
  variant="outlined"
  size="small"
  value={texto}
  onChange={(e) => atualizarMetodologia(index, e.target.value)}
  placeholder="Digite a metodologia/norma"
  fullWidth
  multiline
  minRows={1}
  maxRows={2}
  sx={{
    "& .MuiInputBase-root": {
      minHeight: "38px",
      alignItems: "center",
    },
    "& textarea": {
      padding: "2px 0",
      fontSize: "13px",
      lineHeight: "18px",
    },
  }}
/>

        {!somenteLeitura && (
          <Tooltip title="Remover metodologia">
            <span>
              <IconButton
                size="small"
                color="error"
                onClick={() => removerMetodologia(index)}
                disabled={metodologiasRelatorio.length <= 1}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  ))}
</div>
            <div className="observacoes-importantes-editor">
              <div className="observacoes-importantes-topo">
                <strong>
                  OBSERVAÇÕES IMPORTANTES - ITEM {dosagem.caldaCimento ? "4" : "5"} DO RELATÓRIO:
                </strong>
                {!somenteLeitura && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={adicionarObservacaoImportante}
                  >
                    Novo subitem
                  </Button>
                )}
              </div>

              {observacoesImportantes.map((item, index) => (
                <div className="observacao-importante-item" key={`observacao-importante-${index}`}>
                  <div className="observacao-importante-cabecalho">
                    <span>{`${dosagem.caldaCimento ? "4" : "5"}.${index + 1}`}</span>
                    <TextField
                      variant="standard"
                      value={item.titulo}
                      onChange={(e) => atualizarObservacaoImportante(index, "titulo", e.target.value)}
                      placeholder="Título do subitem"
                      fullWidth
                    />
                    {!somenteLeitura && (
                      <Tooltip title="Remover subitem">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removerObservacaoImportante(index)}
                            disabled={observacoesImportantes.length <= 1}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </div>
                  <TextField
                    variant="outlined"
                    value={item.texto}
                    onChange={(e) => atualizarObservacaoImportante(index, "texto", e.target.value)}
                    placeholder="Descrição do subitem"
                    fullWidth
                    multiline
                    minRows={2}
                  />
                </div>
              ))}
            </div>

          </section>
        </Paper>
      </Box>
    </>
  );
}
